import React from "react";
import { flushSync } from "react-dom";
import { Link, useOutletContext } from "react-router-dom";
import {
  CONTROL_ROOM_TABS,
  CONTROL_ROOM_TAB_EVENT,
  readControlRoomTab,
  useRefreshOnReturn,
  saveControlRoomTab,
} from "../lib/controlRoomTabs";
import ConnectorsSection from "../components/ConnectorsSection";
import TeamDirectorySettings from "../components/TeamDirectorySettings";
import { apiRequest, loadWorkspaceDocument, prepareWorkspaceContextFile, saveData } from "../api/awsClient";
import { sanitizeTeamMembers } from "../lib/teamDirectory";
import { toast } from "../lib/toast";
import { Auth } from "../helpers/auth";
import { changedSettings, directoryBaseOf } from "../lib/workspaceSettings";
import {
  countWritableDirectoryRows,
  readDirectorySyncFailure,
} from "../lib/workspaceDoc";
import { LS_KEYS } from "../App";
import AutomationToolPicker from "../components/AutomationToolPicker";
import AutomationChips, {
  useCalendarConnections,
  useMeetingOrganizers,
} from "../components/AutomationChips";
import {
  effectiveMeetingPerson,
  meetingCalendarRow,
  meetingPersonCalendars,
  personName,
  possessive,
} from "../lib/meetingCalendar";
import ToolChoiceField from "../components/ToolChoiceField";
import { PilotBaselineBanner, usePilotData } from "../components/PilotCard";
import { todayInZone, workspaceTimeZone } from "../lib/pilot";
import {
  chatToolFor,
  dailyPingChips,
  escalationChips,
  meetingChips,
  reportDeliveryChips,
  taskChips,
  taskToolFor,
} from "../lib/automationChips";
import {
  calendarToolsRow,
  chatToolsRow,
  pickChatBackend,
  taskToolsRow,
  withoutTaskOverride,
} from "../lib/toolsInUse";
import SaveToast from "../components/SaveToast";
import CustomAutomations from "../components/CustomAutomations";
import MeetingNotetakerCard from "../components/MeetingNotetakerCard";
import EmailTrackingMailboxes from "../components/EmailTrackingMailboxes";
import {
  EMAIL_RETENTION_OPTIONS,
  normalizeEmailRetentionDays,
} from "../lib/emailRetention";
import CrIcon from "../components/CrIcon";
import FancySelect from "../components/FancySelect";
import {
  DAYS,
  DayPills,
  FancyMiniSelect,
  FancyTZSelect,
  TIMES,
  getSupportedTimeZones,
} from "../components/HoursPickers";
import { pickTaskBackend, taskToolStates } from "../lib/taskTools";
import chatgptLogo from "../images/chat-gpt-logo-vector-black-3ded.png";
import claudeLogo from "../images/claude-ai-icon-65aa.png";

/* ---------- helpers for cron-ish schedule ---------- */
function parseCronTZ(spec) {
  const m = String(spec || "").match(
    /^(SUN|MON|TUE|WED|THU|FRI|SAT)\s+(\d{2}):(\d{2})\s+([\w/]+)$/i,
  );
  if (!m) return null;
  return { day: m[1].toUpperCase(), time: `${m[2]}:${m[3]}`, tz: m[4] };
}
function buildCronTZ({ day, time, tz }) {
  return `${day} ${time} ${tz}`;
}
function parseReportSchedule(spec) {
  const s = String(spec || "").trim();

  // DAILY 09:00 America/Chicago
  let m = s.match(/^DAILY\s+(\d{2}):(\d{2})\s+([\w/]+)$/i);
  if (m) return { freq: "daily", time: `${m[1]}:${m[2]}`, tz: m[3] };

  // MWF 09:00 America/Chicago
  m = s.match(/^MWF\s+(\d{2}):(\d{2})\s+([\w/]+)$/i);
  if (m) return { freq: "three_day", time: `${m[1]}:${m[2]}`, tz: m[3] };

  // MONTHLY 1 09:00 America/Chicago
  m = s.match(/^MONTHLY\s+(\d{1,2})\s+(\d{2}):(\d{2})\s+([\w/]+)$/i);
  if (m) return { freq: "three_day", time: `${m[2]}:${m[3]}`, tz: m[4] };

  // Back-compat WEEKLY format: FRI 09:00 America/Chicago
  m = s.match(/^(SUN|MON|TUE|WED|THU|FRI|SAT)\s+(\d{2}):(\d{2})\s+([\w/]+)$/i);
  if (m)
    return {
      freq: "weekly",
      day: m[1].toUpperCase(),
      time: `${m[2]}:${m[3]}`,
      tz: m[4],
    };

  return null;
}

function buildReportSchedule({ freq, day, time, tz }) {
  if (freq === "daily") return `DAILY ${time} ${tz}`;
  if (freq === "three_day") return `MWF ${time} ${tz}`;
  // weekly stays in old format for compatibility
  return `${day} ${time} ${tz}`;
}

const MAX_AI_CONTEXT_FILES = 12;

function formatFileBytes(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
}

function mergeContextFiles(existing = [], additions = []) {
  const next = [];
  const byKey = new Map();
  for (const entry of existing || []) {
    if (!entry?.id) continue;
    const key = `${String(entry.name || "").toLowerCase()}|${String(entry.mimeType || "").toLowerCase()}`;
    byKey.set(key, entry);
  }
  for (const entry of additions || []) {
    if (!entry?.id) continue;
    const key = `${String(entry.name || "").toLowerCase()}|${String(entry.mimeType || "").toLowerCase()}`;
    byKey.set(key, entry);
  }
  for (const value of byKey.values()) {
    next.push(value);
  }
  return next
    .sort((a, b) =>
      String(b?.uploadedAt || "").localeCompare(String(a?.uploadedAt || "")),
    )
    .slice(0, MAX_AI_CONTEXT_FILES);
}
/* ---------- small helpers for business hours ---------- */
const BH_DEFAULT_DAYS = ["MON", "TUE", "WED", "THU", "FRI"];
const BRAIN_CONF_DEFAULTS = {
  autoExecuteConfidence: 0.72,
  askClarifyConfidence: 0.55,
  minMeetingConfidence: 0.78,
  minEscalationConfidence: 0.75,
};
const clampPct = (n) => Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
const toMin = (hhmm) => {
  const [h, m] = String(hhmm || "00:00")
    .split(":")
    .map(Number);
  return h * 60 + (m || 0);
};

const MINUTES_PER_WEEK = 7 * 24 * 60; // 10080
const BH_DAY_TOKENS = DAYS.map(([v]) => v);
// 0=SUN..6=SAT, the shape older workspace docs store their business-hours days
// in. The backend's poll gate (automationWorker's resolvePollBusinessWindow)
// accepts it, so this card has to as well or it warns "Not in effect" about
// hours the backend is honouring. Tokens are this card's own encoding --
// pollTicksPerWeek and pollWeekCoverage count days by token, and the gate the
// card describes compares tokens too. Meetings book inside these same hours
// (server/calendar/meetingHours.js).
const BH_DAY_BY_INDEX = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function isUsableTz(tz) {
  if (typeof tz !== "string" || !tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// Mirrors resolvePollBusinessWindow in backend/server/automationWorker.js, and
// like it substitutes NOTHING: it must be given the RAW stored
// slackPrefs.businessHours, never a copy that has already had a browser
// timezone and 09:00-17:30 filled in. The backend fails OPEN on a window it
// cannot resolve and keeps polling around the clock, so validating a defaulted
// object makes the "Not in effect" warning unreachable and the card promises a
// saving that is not happening.
//
// Timezone fallback is the one the worker's gate uses -- it resolves
// [businessHours.tz, slackPrefs.tz, settings.tz] -- as far as the hub can see
// it: businessHours.tz, then slackPrefs.tz. The third link (settings.tz) and
// the legacy top-level settings.businessHours the backends fall back to are
// not in this page's outlet context; nothing in the hub writes either, and
// erring towards "Not in effect" only ever under-promises. Numeric day entries
// ARE mirrored, because the backend accepts them: a warning about hours the
// backend is using would not be under-promising, it would be wrong.
function resolvePollWindow(slackPrefs) {
  const prefs = slackPrefs && typeof slackPrefs === "object" ? slackPrefs : {};
  const bh =
    prefs.businessHours &&
    typeof prefs.businessHours === "object" &&
    !Array.isArray(prefs.businessHours)
      ? prefs.businessHours
      : null;
  if (!bh) return { ok: false, reason: "unset" };
  const tz = [bh.tz, prefs.tz].find(isUsableTz);
  if (!tz) return { ok: false, reason: "timezone" };
  if (!HHMM_RE.test(String(bh.start || ""))) return { ok: false, reason: "times" };
  if (!HHMM_RE.test(String(bh.end || ""))) return { ok: false, reason: "times" };
  if (toMin(bh.end) <= toMin(bh.start)) return { ok: false, reason: "times" };
  const days = Array.from(
    new Set(
      (Array.isArray(bh.days) ? bh.days : [])
        .map((d) =>
          typeof d === "number"
            ? BH_DAY_BY_INDEX[d]
            : String(d || "").trim().toUpperCase(),
        )
        .filter((d) => BH_DAY_TOKENS.includes(d)),
    ),
  );
  if (!days.length) return { ok: false, reason: "days" };
  return { ok: true, tz, start: bh.start, end: bh.end, days };
}

// Pass win = null for "around the clock". End-inclusive, matching
// isWithinWorkerBusinessHours (`mins <= end`), so 09:00-17:30 is 511 minutes.
function pollTicksPerWeek(pollMinutes, win) {
  const interval = Math.max(1, Math.floor(Number(pollMinutes) || 5));
  if (!win) return Math.round(MINUTES_PER_WEEK / interval);
  const days = new Set(
    (win.days || [])
      .map((d) => String(d).toUpperCase())
      .filter((d) => BH_DAY_TOKENS.includes(d)),
  );
  const minutesPerDay = toMin(win.end) - toMin(win.start) + 1;
  return Math.round((minutesPerDay * days.size) / interval);
}

// Minutes east of UTC for `tz`. Read once per estimate; a DST change inside the
// week shifts part of that week by an hour, which a weekly estimate can wear.
function tzOffsetMinutes(tz, at = new Date()) {
  try {
    const local = new Date(at.toLocaleString("en-US", { timeZone: tz }));
    const utc = new Date(at.toLocaleString("en-US", { timeZone: "Etc/UTC" }));
    return Math.round((local.getTime() - utc.getTime()) / 60000);
  } catch {
    return null;
  }
}

/**
 * The worker gates a scheduled tick on the UNION of the business hours read in
 * the workspace's timezone and in each teammate's own timezone
 * (pollBusinessWindowsForTick in automationWorker.js), because a daily status
 * ping is pinned inside the TEAMMATE's working day and would otherwise never be
 * delivered. This reproduces that union so the card can quote the ticks the
 * worker will really run, and can name any teammate zone whose working day does
 * not overlap the workspace's at all.
 *
 * @returns {{minutes:number, zones:string[], disjointZones:string[]}}
 */
function pollWeekCoverage(win, memberZones = []) {
  const dayIndex = Object.fromEntries(BH_DAY_TOKENS.map((d, i) => [d, i]));
  const startMin = toMin(win.start);
  const endMin = toMin(win.end);
  const markZone = (tz) => {
    const offset = tzOffsetMinutes(tz);
    if (offset === null) return null;
    const marks = new Set();
    for (const day of win.days) {
      const base = (dayIndex[day] ?? 0) * 1440;
      for (let m = startMin; m <= endMin; m += 1) {
        marks.add(((base + m - offset) % MINUTES_PER_WEEK + MINUTES_PER_WEEK) % MINUTES_PER_WEEK);
      }
    }
    return marks;
  };

  const workspace = markZone(win.tz) || new Set();
  const union = new Set(workspace);
  const zones = [win.tz];
  const disjointZones = [];
  for (const tz of memberZones) {
    if (zones.includes(tz)) continue;
    const marks = markZone(tz);
    if (!marks) continue;
    zones.push(tz);
    let overlaps = false;
    for (const minute of marks) {
      if (workspace.has(minute)) overlaps = true;
      union.add(minute);
    }
    if (!overlaps) disjointZones.push(tz);
  }
  return { minutes: union.size, zones, disjointZones };
}

function AIProviderLogo({ provider }) {
  const src = provider === "claude" ? claudeLogo : chatgptLogo;
  const alt = provider === "claude" ? "Claude" : "ChatGPT";
  return (
    <span aria-hidden className="crLogoTile">
      <img
        src={src}
        alt={alt}
        style={{
          width: "82%",
          height: "82%",
          objectFit: "contain",
          display: "block",
        }}
      />
    </span>
  );
}

// `model` is only a placeholder label until GET /api/ai/models loads; the real
// model list and defaults come from the backend catalog. GitHub Models
// ("Copilot") was retired by GitHub on 2026-07-30 and is no longer offered.
const AI_PROVIDER_OPTIONS = [
  {
    value: "chatgpt",
    label: "ChatGPT",
    vendor: "OpenAI",
    model: "GPT-5.4 mini",
    blurb: "Fast, polished, and versatile.",
  },
  {
    value: "claude",
    label: "Claude",
    vendor: "Anthropic",
    model: "Claude Opus 4.6",
    blurb: "Recommended for the best results.",
  },
];

function MidiKnob({ label, value, onChange, help, disabled = false }) {
  const pct = clampPct(value);
  const angle = -135 + (pct / 100) * 270;
  const [dragging, setDragging] = React.useState(false);
  const dragRef = React.useRef(null);

  const commit = React.useCallback(
    (next) => onChange?.(clampPct(next)),
    [onChange],
  );

  const angleFromPointer = React.useCallback(
    (clientX, clientY, el) => {
      if (!el?.getBoundingClientRect) return pct;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const theta = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI; // -180..180
      return (theta + 360) % 360; // 0..359 (0 = right)
    },
    [pct],
  );

  const isInsideKnob = React.useCallback((clientX, clientY, el) => {
    if (!el?.getBoundingClientRect) return false;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const radius = Math.min(rect.width, rect.height) / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    return dx * dx + dy * dy <= radius * radius;
  }, []);

  const onPointerDown = React.useCallback(
    (e) => {
      if (disabled) return;
      if (!isInsideKnob(e.clientX, e.clientY, e.currentTarget)) return;
      e.preventDefault();
      e.currentTarget.focus();
      dragRef.current = {
        pointerId: e.pointerId,
        target: e.currentTarget,
        lastAngle: angleFromPointer(e.clientX, e.clientY, e.currentTarget),
        lastPct: pct,
      };
      setDragging(true);
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {}
    },
    [angleFromPointer, disabled, isInsideKnob, pct],
  );

  const onPointerMove = React.useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.pointerId != null && e.pointerId !== d.pointerId) return;
      const target = d.target || e.currentTarget;
      const currentAngle = angleFromPointer(e.clientX, e.clientY, target);

      // Only update while pointer is inside the knob.
      if (!isInsideKnob(e.clientX, e.clientY, target)) {
        d.lastAngle = currentAngle;
        return;
      }

      let delta = currentAngle - d.lastAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;

      const nextPct = clampPct(d.lastPct + (delta / 270) * 100);
      d.lastAngle = currentAngle;
      d.lastPct = nextPct;
      commit(nextPct);
    },
    [angleFromPointer, commit, isInsideKnob],
  );

  const stopDrag = React.useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    if (
      d.pointerId != null &&
      e?.pointerId != null &&
      e.pointerId !== d.pointerId
    )
      return;
    dragRef.current = null;
    setDragging(false);
    try {
      if (e?.currentTarget && e?.pointerId != null) {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      }
    } catch {}
  }, []);

  const onKeyDown = React.useCallback(
    (e) => {
      if (disabled) return;
      const step = e.shiftKey ? 5 : 1;
      if (e.key === "ArrowUp" || e.key === "ArrowRight") {
        e.preventDefault();
        commit(pct + step);
      } else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
        e.preventDefault();
        commit(pct - step);
      } else if (e.key === "Home") {
        e.preventDefault();
        commit(0);
      } else if (e.key === "End") {
        e.preventDefault();
        commit(100);
      }
    },
    [commit, disabled, pct],
  );

  return (
    <div className="midiKnobCard">
      <div className="midiKnobLabel">{label}</div>
      <div
        className={`midiKnob ${dragging ? "dragging" : ""} ${
          disabled ? "disabled" : ""
        }`}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-disabled={disabled}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onKeyDown={onKeyDown}
        style={{
          "--knob-angle": `${angle}deg`,
        }}
      >
        <span className="midiKnob__ticks" aria-hidden />
        <span className="midiKnob__ring" aria-hidden />
        <span className="midiKnob__value">{pct}%</span>
      </div>
      <div className="muted small">{help}</div>
    </div>
  );
}

// ---- read-only formatting for the Inbox status panel ----
function timeAgoLabel(iso) {
  const ts = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(ts)) return "";
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

function dateTimeLabel(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Drop reasons the backend attributes to a workspace (aiInboxPolicy.js).
const AI_INBOX_DROP_LABELS = {
  assistant_off: "Email assistant was off",
  sender_not_allowed:
    "Sender isn't allowed by your 'Who may email BossDrop' setting",
  sender_unverified: "Couldn't confirm the sender really sent it",
  daily_cap: "Daily reply limit reached",
};

// The chip beside the inbox config warning. The server says WHICH warning it is
// (configWarningKind), because the tone does not follow from senderVerification:
// the healthy Spacemail default is "verified_only" AND carries the SPF warning,
// and keying the tone off that combination painted a server that was verifying
// DKIM correctly as a red "Server not configured".
//
// Red is reserved for "nothing on this server can verify anything" — the one
// state where mail is being answered unchecked. `kind` is null on a server that
// predates the field, so the tone falls back to the mode it reports.
const AI_INBOX_WARNING_LABELS = {
  cannot_verify: "Server not configured",
  library_unavailable: "Signature check off",
  spf_unconfigured: "SPF not configured",
};
function aiInboxWarningChip(poller) {
  const kind = poller?.configWarningKind || "";
  const monitorOnly = poller?.senderVerification === "monitor_only";
  return {
    label: AI_INBOX_WARNING_LABELS[kind] || "Server not configured",
    className:
      kind === "cannot_verify" || (!kind && monitorOnly) ? "chip bad" : "chip warn",
  };
}

// The "(last: …)" suffix on one drop line. For `sender_unverified` the stored
// detail is only the DOMAIN the message claimed to come from, read off mail
// nothing authenticated, so it is worded as a claim rather than printed as if
// BossDrop knew who sent it.
function dropDetailSuffix(reason, detail) {
  const value = String(detail || "").trim();
  if (!value) return "";
  if (reason !== "sender_unverified") return ` (last: ${value})`;
  if (value === "(no domain)") {
    return " (last: a message with no sender address)";
  }
  return ` (last: someone claiming to be @${value})`;
}

function AutomationRuleCard({
  icon,
  title,
  description,
  enabled,
  onToggle,
  statusLabel,
  statusTone = "muted",
  // Strings or { tool, label } (lib/automationChips.js), or an element that
  // renders its own chip row.
  chips = [],
  // For a card outside the admin fieldset (the Email assistant).
  toggleDisabled = false,
  children, // ✅ NEW
}) {
  return (
    <div className="ruleCard">
      {/* Exactly the header shape the Executive report, Business hours and
          Meeting notes cards use: .crCardHead wrapping a __main (title +
          description) and a __aside that pins the status chip next to the
          switch. Nesting .crCardHead inside __main instead -- as this card
          used to -- puts the chip at the right edge of the TEXT column, where
          a long title wraps it onto its own line. */}
      <div className="crCardHead">
        <div className="crCardHead__main">
          <div className="ruleTitle crCardHead__title">
            {icon ? <CrIcon name={icon} /> : null}
            {title}
          </div>
          <div className="muted small crCardHead__sub">{description}</div>
        </div>

        <div className="crCardHead__aside">
          <span className={`chip ${statusTone}`}>{statusLabel}</span>
          <input
            className="switchInput"
            type="checkbox"
            checked={!!enabled}
            disabled={toggleDisabled}
            onChange={(e) => onToggle?.(e.target.checked)}
            aria-label={`Toggle ${title}`}
          />
        </div>
      </div>

      {React.isValidElement(chips) ? chips : <AutomationChips chips={chips} />}

      {/* ✅ NEW: per-automation tool preference UI */}
      {children ? <div className="ruleCardBody">{children}</div> : null}
    </div>
  );
}

export default function SettingsPage() {
  const {
    automationMode,
    setAutomationMode,
    teamMembers,
    setTeamMembers,
    asanaConnected,
    setAsanaConnected,
    setAsanaData,
    jiraConnected,
    setJiraConnected,
    setJiraData,
    hubspotConnected,
    setHubspotConnected,
    pipedriveConnected,
    setPipedriveConnected,
    linearConnected,
    setLinearConnected,
    setLinearData,
    clickupConnected,
    setClickupConnected,
    setClickupData,
    notionConnected,
    setNotionConnected,
    setNotionData,
    mondayConnected,
    setMondayConnected,
    setMondayData,
    githubConnected,
    setGithubConnected,
    slackConnected,
    setSlackConnected,
    slackPrefs,
    setSlackPrefs,
    asanaPrefs,
    setAsanaPrefs,
    jiraPrefs,
    setJiraPrefs,
    hubspotPrefs,
    setHubspotPrefs,
    pipedrivePrefs,
    setPipedrivePrefs,
    linearPrefs,
    setLinearPrefs,
    clickupPrefs,
    setClickupPrefs,
    notionPrefs,
    setNotionPrefs,
    mondayPrefs,
    setMondayPrefs,
    githubPrefs,
    setGithubPrefs,
    pullSlack,
    teamsConnected,
    setTeamsConnected,
    teamsPrefs,
    setTeamsPrefs,
    pullTeams,
    projectScope,
    setProjectScope,
    overdueDaysThreshold,
    setOverdueDaysThreshold,
    riskAmountThreshold,
    setRiskAmountThreshold,
    taskWriteBackend,
    setTaskWriteBackend,
    // settings.chatWriteBackend has no picker of its own any more: each chat
    // card saves its own tool, and this is the fallback for a card that has
    // none (the server's resolveChatBackend). It is kept and saved as is.
    chatWriteBackend,
    // settings.calendarWriteBackend: "google" | "microsoft" | null (default).
    calendarWriteBackend = null,
    setCalendarWriteBackend = null,
    // settings.meetingCalendarEmail: whose calendar BossDrop's own meetings
    // are booked from, or null for the owner (lib/meetingCalendar.js).
    meetingCalendarEmail = null,
    setMeetingCalendarEmail = null,
    // settings.pilot (lib/pilot.js), or null when no pilot was ever started.
    // Edited on the KPIs page; read here for the banner. The autosave below
    // sends it only if it changes while this page is open.
    pilot = null,
    pollMinutes,
    setPollMinutes,
    automationToolPrefs,
    setAutomationToolPrefs,
    aiInbox,
    setAiInbox,
    meetingNotetaker,
    setMeetingNotetaker,
    // settings.meetingHours: no longer used (meetings follow business
    // hours); kept so the doc saves exactly what it did before.
    meetingHours,
    setMeetingHours = null,
    brainPrefs,
    setBrainPrefs,
    aiContextFiles,
    setAiContextFiles,
    emailTrackingDomains,
    setEmailTrackingDomains,
    emailRetentionDays = 30,
    setEmailRetentionDays = null,
    theme,
    aiText,
    aiLoading,
    generateAiRewrite,
    apiBase,
    currentUserEmail,
    workspaceOwnerEmail,
    workspaceSecurityLevel,
    workspacePermissions,
    canManageWorkspace: canManageWorkspaceAccess,
    workspaceDocStatus = "ready",
    // The last refusal from mirroring the Team directory into the access map,
    // from the BOOTSTRAP mirror (App.js) or a save on this page, or null. It
    // lives in App, not here: this page remounts on every navigation, and a
    // local copy either brought a fixed refusal back or lost a real one.
    workspaceDirectorySyncError = null,
    setWorkspaceDirectorySyncError = null,
    // App-level flag: a deliberate "remove everyone" is still owed to the
    // access map (see mirrorDirectory). Survives this page remounting.
    directoryClearPendingRef = null,
  } = useOutletContext();
  // A workspace document that did not load is NOT an empty workspace. Until the
  // read succeeds every field on this page holds a DEFAULT, and this page
  // autosaves -- so one failed read on bootstrap plus one keystroke here wrote
  // those defaults over the real workspace, Team directory included. Every
  // write path below is already gated on `canManageWorkspace`, so folding the
  // document's state into it closes all of them at once, and the disabled
  // fieldsets make it visible. The banner explaining it is rendered once, above
  // this page, by the layout.
  const workspaceDocReady = workspaceDocStatus === "ready";
  const canManageWorkspace = !!canManageWorkspaceAccess && workspaceDocReady;
  // OWNER ONLY: the Team directory IS the workspace's access map, so editing it
  // is not workspace management, it is handing out access. An admin keeps every
  // other setting on this page. The server refuses the write either way (403
  // workspace_owner_only at the sync route, and PUT /api/workspace/doc drops an
  // incoming teamMembers); this is what stops the hub offering the control.
  const canEditDirectory =
    !!workspacePermissions?.canEditDirectory && workspaceDocReady;
  // Still admin-reachable: inviting somebody the OWNER already listed decides
  // nobody's access, it only tells them they have it.
  const canInviteTeammates = !!workspacePermissions?.canInviteTeammates;
  const saveTimer = React.useRef(null);
  const hasHydratedSettingsRef = React.useRef(false);
  const lastPersistedSettingsRef = React.useRef("");
  const lastQueuedSettingsRef = React.useRef("");
  const pendingSettingsRef = React.useRef(null);
  const saveInFlightRef = React.useRef(false);
  // The Team directory the ACCESS MAP is known to hold, i.e. the last list a
  // sync actually accepted (seeded from the document this page hydrated with,
  // which App.js mirrors on bootstrap). Kept apart from
  // `lastPersistedSettingsRef`, which tracks the DOCUMENT: the two diverge
  // exactly when a mirror is refused, and using the document's copy would make
  // a failed "remove everyone" permanent, because the next save would see an
  // already-empty previous list and never send the clear again.
  const lastMirroredDirectoryRef = React.useRef([]);
  // The `teamMembers` array the last persist() call carried (by identity), so
  // persist can tell a directory edit from an edit to any other setting.
  const persistedDirectoryRef = React.useRef(null);
  const saveStatusTimerRef = React.useRef(null);
  const [saveStatus, setSaveStatus] = React.useState("idle");
  // The Team directory panel's banner: `{ error, email, status, scope }` or
  // null. scope "access": the document saved and the ACCESS MAP did not.
  // scope "doc": PUT /api/workspace/doc refused an address the save added, so
  // NOTHING saved. Read from and written to App (see the outlet context above).
  const directorySyncError = workspaceDirectorySyncError || null;
  const directorySyncErrorRef = React.useRef(directorySyncError);
  directorySyncErrorRef.current = directorySyncError;
  const reportDirectorySync = React.useCallback(
    (next) => {
      if (typeof setWorkspaceDirectorySyncError === "function") {
        setWorkspaceDirectorySyncError(next);
      }
    },
    [setWorkspaceDirectorySyncError],
  );
  const fallbackClearPendingRef = React.useRef(false);
  const clearPendingRef = directoryClearPendingRef || fallbackClearPendingRef;
  // Bumped after every mirror the access map ACCEPTED, so the Team directory
  // panel re-reads who has access instead of comparing against a stale read.
  const [directoryMirrorVersion, setDirectoryMirrorVersion] = React.useState(0);
  // Counts saves that landed. What a custom automation's card says comes from the saved
  // settings, so the section reads again when one lands.
  const [settingsSaves, setSettingsSaves] = React.useState(0);
  // Every tab stays mounted, only hidden, so what a card holds and autosave carry on.
  const [section, setSection] = React.useState(readControlRoomTab);
  const chooseSection = (next) => {
    setSection(next);
    saveControlRoomTab(next);
    document.querySelector(".content")?.scrollTo?.({ top: 0, behavior: "auto" });
  };
  React.useEffect(() => {
    const show = (event) => {
      const next = event?.detail?.tab;
      if (!CONTROL_ROOM_TABS.some((t) => t.key === next)) return;
      saveControlRoomTab(next);
      flushSync(() => setSection(next));
    };
    window.addEventListener(CONTROL_ROOM_TAB_EVENT, show);
    return () => window.removeEventListener(CONTROL_ROOM_TAB_EVENT, show);
  }, []);
  const [aiInboxStatus, setAiInboxStatus] = React.useState(null);
  // "loading", not "idle": the fetch runs from a useEffect, which fires AFTER
  // the first paint, so an "idle" start renders one frame of "Couldn't load
  // inbox status." every single time the Control Room mounts.
  const [aiInboxStatusState, setAiInboxStatusState] = React.useState("loading");
  // Half-typed daily reply cap. An <input type="number"> reports "" for both an
  // empty field and a partial entry like "1e", and Number("") is 0 -- writing
  // that through the autosave clamped the workspace to ONE reply a day. The
  // draft is held here and only a value that is already valid is saved as you
  // type; anything else waits for blur.
  const [replyCapDraft, setReplyCapDraft] = React.useState(null);
  const [contextFilesBusy, setContextFilesBusy] = React.useState(false);
  const [contextFilesStatus, setContextFilesStatus] = React.useState("");
  const [contextFilesError, setContextFilesError] = React.useState("");
  const [emailDomainInput, setEmailDomainInput] = React.useState("");
  const [email, setEmail] = React.useState(workspaceOwnerEmail || "");
  const contextFileInputRef = React.useRef(null);
  const POLL_PRESETS = [1, 5, 10, 15, 30, 60];
  // Custom intervals have no upper limit, so spell long ones out -- it makes a
  // typo like 1440 vs 144 obvious before it is saved.
  const formatPollInterval = (mins) => {
    const m = Math.max(1, Math.floor(Number(mins) || 1));
    const d = Math.floor(m / 1440);
    const h = Math.floor((m % 1440) / 60);
    const r = m % 60;
    return [
      d ? `${d} day${d === 1 ? "" : "s"}` : "",
      h ? `${h} hour${h === 1 ? "" : "s"}` : "",
      r ? `${r} minute${r === 1 ? "" : "s"}` : "",
    ]
      .filter(Boolean)
      .join(" ");
  };
  const pollMinutesSafe = Math.max(1, Number(pollMinutes) || 5);

  // tracks whether user chose "Custom…" even if value matches a preset
  const [pollMode, setPollMode] = React.useState(() =>
    POLL_PRESETS.includes(pollMinutesSafe) ? "preset" : "custom",
  );

  // if polling minutes becomes non-preset (e.g., loaded from S3), switch to custom
  React.useEffect(() => {
    if (!POLL_PRESETS.includes(pollMinutesSafe)) setPollMode("custom");
  }, [pollMinutesSafe]);
  // Every task tool: connected or not, ready (set up enough for the worker to
  // write to it) or not, and what is left to set up. The gates live in
  // lib/taskTools.js: Planner needs a tenant, team and plan; Asana a
  // workspace; Jira a site and project; Linear a team to read and a default
  // team; ClickUp, Notion and monday.com (isClickUpPrefsReady,
  // isNotionTaskToolReady, isMondayTaskToolReady on the server) something to
  // read and a default place, one of those, to create tasks in.
  const taskToolStateList = React.useMemo(
    () =>
      taskToolStates(
        {
          planner: teamsConnected,
          asana: asanaConnected,
          jira: jiraConnected,
          linear: linearConnected,
          clickup: clickupConnected,
          notion: notionConnected,
          monday: mondayConnected,
        },
        {
          teamsPrefs,
          asanaPrefs,
          jiraPrefs,
          linearPrefs,
          clickupPrefs,
          notionPrefs,
          mondayPrefs,
        },
      ),
    [
      teamsConnected,
      asanaConnected,
      jiraConnected,
      linearConnected,
      clickupConnected,
      notionConnected,
      mondayConnected,
      teamsPrefs,
      asanaPrefs,
      jiraPrefs,
      linearPrefs,
      clickupPrefs,
      notionPrefs,
      mondayPrefs,
    ],
  );
  const taskToolIsReady = (tool) =>
    !!taskToolStateList.find((s) => s.key === tool)?.ready;
  const plannerConnected = taskToolIsReady("planner");
  const asanaToolConnected = taskToolIsReady("asana");
  const jiraToolConnected = taskToolIsReady("jira");
  const linearToolConnected = taskToolIsReady("linear");
  const clickupToolConnected = taskToolIsReady("clickup");
  const notionToolConnected = taskToolIsReady("notion");
  const mondayToolConnected = taskToolIsReady("monday");
  const teamsToolConnected = !!teamsConnected;
  const slackToolConnected = !!slackConnected;
  // --- Effective backends (match server fallback behavior) ---
  // The wanted chat tool when it is connected, else Teams, else Slack
  // (pickChatBackend in the worker).
  const chatBackendEffective = React.useMemo(
    () =>
      pickChatBackend(chatWriteBackend, {
        teams: teamsToolConnected,
        slack: slackToolConnected,
      }),
    [chatWriteBackend, teamsToolConnected, slackToolConnected],
  );

  // The wanted tool when it is usable, else the first usable one in the
  // server's order planner > asana > jira > linear > clickup > notion > monday
  // (pickTaskBackend in the worker and executePlan). The newer tools come after
  // Linear, so a workspace that already had a task tool keeps writing to it.
  const taskBackendEffective = React.useMemo(
    () =>
      pickTaskBackend(taskWriteBackend, {
        planner: !!plannerConnected,
        asana: !!asanaToolConnected,
        jira: !!jiraToolConnected,
        linear: !!linearToolConnected,
        clickup: !!clickupToolConnected,
        notion: !!notionToolConnected,
        monday: !!mondayToolConnected,
      }),
    [
      taskWriteBackend,
      plannerConnected,
      asanaToolConnected,
      jiraToolConnected,
      linearToolConnected,
      clickupToolConnected,
      notionToolConnected,
      mondayToolConnected,
    ],
  );

  // Meetings are booked on the owner's calendar. Only an admin or the owner
  // may read which calendars that is (canManageCalendarTarget). Read once
  // here for the meetings card's calendar picker and its chips.
  const canReadOwnerCalendar =
    canManageWorkspace ||
    (!!currentUserEmail &&
      String(currentUserEmail).trim().toLowerCase() ===
        String(workspaceOwnerEmail || "").trim().toLowerCase());
  const ownerCalendars = useCalendarConnections({
    apiBase,
    email: workspaceOwnerEmail,
    enabled: canReadOwnerCalendar,
  });
  // The meeting calendar: whose calendar BossDrop's own meetings are booked
  // from (GET /api/calendar/organizers). The meetings card's provider picker
  // and chips follow that person; until the route answers, the owner's.
  const meetingOrganizers = useMeetingOrganizers({ apiBase });
  const meetingPerson = effectiveMeetingPerson(
    meetingOrganizers.organizers,
    meetingCalendarEmail,
  );
  const meetingCalendars = meetingPerson
    ? meetingPersonCalendars(meetingPerson)
    : ownerCalendars;
  const meetingWhose = meetingPerson
    ? possessive(personName(meetingPerson))
    : "the owner's";

  // Pilot mode lives on the KPIs page (components/PilotCard.js); this page
  // shows only the baseline banner on the Automations card. Its phase is the
  // server's when it answers (GET /api/pilot/status), else the calendar's in
  // the workspace's zone. Nothing is shown when no pilot is in its baseline.
  const pilotToday = todayInZone(new Date(), workspaceTimeZone(slackPrefs));
  const pilotData = usePilotData({
    pilot,
    today: pilotToday,
    apiBase,
    ownerEmail: workspaceOwnerEmail,
    withActivity: false,
  });

  const autoReportsEnabled = !!(slackPrefs?.autoReportsEnabled ?? true);
  const execReportStatus = React.useMemo(() => {
    if (!autoReportsEnabled) return { label: "Off", tone: "muted" };
    if (automationMode !== "auto") {
      return { label: "Manual approval", tone: "warn" };
    }
    const executionMode =
      automationToolPrefs?.executionModes?.weeklyReport === "approval"
        ? "approval"
        : "auto";
    if (executionMode === "approval") {
      return { label: "Approval required", tone: "warn" };
    }
    return { label: "Enabled", tone: "ok" };
  }, [automationMode, automationToolPrefs, autoReportsEnabled]);
  // Prefer Teams/Planner by default (your ask)
  /*   const [taskWriteBackend, setTaskWriteBackend] = React.useState(
    slackPrefs?.taskWriteBackend || // if you end up storing in slackPrefs; otherwise ignore
      "planner",
  );
  const [chatWriteBackend, setChatWriteBackend] = React.useState(
    slackPrefs?.chatWriteBackend || "teams",
  ); */

  React.useEffect(() => {
    setEmail(workspaceOwnerEmail || "");
  }, [workspaceOwnerEmail]);

  React.useEffect(() => {
    clearTimeout(saveTimer.current);
    clearTimeout(saveStatusTimerRef.current);
    hasHydratedSettingsRef.current = false;
    lastPersistedSettingsRef.current = "";
    lastQueuedSettingsRef.current = "";
    pendingSettingsRef.current = null;
    saveInFlightRef.current = false;
    lastMirroredDirectoryRef.current = [];
    persistedDirectoryRef.current = null;
    setSaveStatus("idle");
    // The directory sync banner is NOT cleared here. This effect runs on every
    // mount, so clearing it would wipe a bootstrap refusal each time the
    // Control Room opens. An account switch reloads the page, which starts
    // App's copy fresh.
  }, [email, canManageWorkspace]);

  const getExecSummary = React.useCallback(
    async (opts = {}) => {
      if (aiText?.trim() && !aiLoading && !opts?.force) return aiText.trim();
      const t = await generateAiRewrite?.(opts); // ✅ forward opts
      const out = (typeof t === "string" ? t : "")?.trim?.() || "";
      return out || aiText?.trim?.() || "";
    },
    [aiText, aiLoading, generateAiRewrite],
  );

  // Mirror a saved Team directory into the server's access map. OWNER ONLY:
  // for an admin the route is a guaranteed 403 (`workspace_owner_only`), and
  // firing it anyway would put a failed security call in the audit log on
  // every settings save an admin makes.
  //
  // What is sent is decided by WRITABLE rows, counted with the server's own
  // rule (countWritableDirectoryRows: email-shaped, not the owner's own
  // address, de-duplicated), never by `directory.length`. A directory that
  // still shows the owner's card, or a row with no usable address, yields zero
  // rows on the server; sent as an ordinary save it is refused
  // (`directory_sync_empty_refused`) and the last real teammate keeps access.
  //   - writable rows now                      -> ordinary sync of the list
  //   - none now, but the access map held some -> the explicit
  //     `{ teamMembers: [], clearDirectory: true }`, which the server audits
  //     as `workspace_directory_cleared` and which keeps the owner's record
  //   - none now and none before               -> nothing to apply
  // "The access map held some" is the last list a sync ACCEPTED (not the
  // document's), plus App's clear-pending flag, which survives this page
  // remounting. The clear is reachable only from an owner's own removal (the
  // card's delete asks first when it empties the directory), from the
  // banner's "Try again", or from the explicit "Remove their access" button
  // (`explicitClear`); never from a failed read, because every save path here
  // is gated on `workspaceDocStatus === "ready"`.
  //
  // Answers "ok" | "skipped" | "failed". Nothing is swallowed: a refusal names
  // the offending address on the Team directory panel.
  const mirrorDirectory = React.useCallback(
    async (directory, { keepalive = false, explicitClear = false, onlyIfChanged = false } = {}) => {
      if (!canEditDirectory || !email) return "skipped";
      const list = Array.isArray(directory) ? directory : [];
      const nextWritable = countWritableDirectoryRows(list, email);
      const prevWritable = countWritableDirectoryRows(
        lastMirroredDirectoryRef.current,
        email,
      );
      let body = null;
      if (nextWritable > 0) {
        // Every save ends up here, and the sync removes anyone not on the list. A
        // list the access map already holds stays home on a save, or a window that
        // loaded before a teammate was added would take their access away by saving
        // anything at all. After a refusal it goes again, to clear it. "Try again"
        // and "Remove their access" always go: an unchanged list is how an address
        // the directory does not hold loses its access.
        if (
          onlyIfChanged &&
          directorySyncErrorRef.current?.scope !== "access" &&
          JSON.stringify(list) === JSON.stringify(lastMirroredDirectoryRef.current)
        ) {
          return "skipped";
        }
        body = { teamMembers: list };
      } else if (prevWritable > 0 || clearPendingRef.current || explicitClear) {
        body = { teamMembers: [], clearDirectory: true };
      }
      if (!body) {
        // Nothing to apply, so a refusal of an earlier owner-only or
        // address-less directory is stale.
        if (directorySyncErrorRef.current?.error === "directory_sync_empty_refused") {
          reportDirectorySync((prev) =>
            prev?.error === "directory_sync_empty_refused" ? null : prev,
          );
        }
        return "skipped";
      }
      try {
        await Auth.syncWorkspaceMemberships(body, { keepalive });
        lastMirroredDirectoryRef.current = nextWritable > 0 ? list : [];
        clearPendingRef.current = false;
        // A doc-scoped refusal is about the document, not this mirror; it is
        // cleared by the next document save that lands.
        reportDirectorySync((prev) => (prev?.scope === "doc" ? prev : null));
        setDirectoryMirrorVersion((version) => version + 1);
        return "ok";
      } catch (syncError) {
        // The document saved; the ACCESS MAP did not, and the two now
        // disagree. `lastMirroredDirectoryRef` is deliberately NOT advanced,
        // and a refused clear sets App's flag, so the next save (on this mount
        // or the next one) sends the same mirror again.
        if (body.clearDirectory === true) clearPendingRef.current = true;
        reportDirectorySync({
          ...readDirectorySyncFailure(syncError),
          scope: "access",
        });
        return "failed";
      }
    },
    [canEditDirectory, clearPendingRef, email, reportDirectorySync],
  );

  // Take on the directory as the server holds it, with nothing to save or push: it
  // counts as already saved, and as what the access map already holds.
  const adoptDirectory = React.useCallback(
    (list) => {
      try {
        lastPersistedSettingsRef.current = JSON.stringify({
          ...JSON.parse(lastPersistedSettingsRef.current),
          teamMembers: list,
        });
      } catch {
        return;
      }
      lastMirroredDirectoryRef.current = list;
      persistedDirectoryRef.current = list;
      setTeamMembers(list);
    },
    [setTeamMembers],
  );

  const flushPendingSave = React.useCallback(
    async ({ keepalive = false } = {}) => {
      if (!canManageWorkspace || !email) return;
      if (saveInFlightRef.current) return;
      const nextSettings = pendingSettingsRef.current;
      const queuedSerialized = lastQueuedSettingsRef.current;
      if (!nextSettings || !queuedSerialized) return;

      saveInFlightRef.current = true;
      pendingSettingsRef.current = null;
      clearTimeout(saveTimer.current);
      clearTimeout(saveStatusTimerRef.current);
      setSaveStatus("saving");

      try {
        // Only what this window changed (lib/workspaceSettings.js). The rest of its
        // copy may be older than what the server holds now.
        const changed = changedSettings(lastPersistedSettingsRef.current, nextSettings);
        // A directory edit also says what it started from: the server applies only
        // what this window changed to the list as stored now, and answers with the
        // result, which is what the access map gets.
        const directoryBase = changed.teamMembers ? directoryBaseOf(lastPersistedSettingsRef.current) : null;
        let directoryNow = null;
        if (Object.keys(changed).length) {
          const payload = {
            updatedAt: new Date().toISOString(),
            settings: changed,
            ...(directoryBase ? { directoryBase } : {}),
          };
          const saved = await saveData(email, payload, { keepalive });
          if (changed.teamMembers && Array.isArray(saved?.settings?.teamMembers)) {
            directoryNow = sanitizeTeamMembers(saved.settings.teamMembers);
          }
        }
        const directoryMoved =
          !!directoryNow &&
          JSON.stringify(directoryNow) !== JSON.stringify(sanitizeTeamMembers(changed.teamMembers));
        // The document landed, so a refusal of an earlier attempt at it is
        // over. Cleared BEFORE the mirror, which may be skipped entirely.
        if (directorySyncErrorRef.current?.scope === "doc") {
          reportDirectorySync((prev) => (prev?.scope === "doc" ? null : prev));
        }
        // Mirror the saved Team directory into the access map (see
        // mirrorDirectory for what is sent). Runs on keepalive saves too: a
        // tab that was only hidden is still alive and finishes the chain, and
        // `keepalive` lets the sync complete if the tab closes mid-request.
        // (A tab that is really unloading never reaches this line; removing
        // the last teammate flushes at once rather than after the debounce
        // for that reason, and the Team directory panel lists anyone who
        // still has access without being in the directory.)
        if (canEditDirectory) {
          await mirrorDirectory(
            directoryMoved
              ? directoryNow
              : Array.isArray(nextSettings.teamMembers)
                ? nextSettings.teamMembers
                : [],
            { keepalive, onlyIfChanged: true },
          );
        }
        lastPersistedSettingsRef.current = queuedSerialized;
        const nothingNewerQueued = lastQueuedSettingsRef.current === queuedSerialized;
        if (nothingNewerQueued) {
          lastQueuedSettingsRef.current = "";
        }
        // Meeting notes consent is one-shot: it rides only the save made by
        // the "Turn on" click. Once that save lands it is dropped from local
        // state, so a tab left open can never re-accept consent (and turn
        // the notetaker back on) after another admin turned it off. The
        // stripped snapshot counts as persisted, so no extra save is sent.
        if (nextSettings?.meetingNotetaker?.consentAccepted === true) {
          const { consentAccepted: _sent, ...notetakerRest } =
            nextSettings.meetingNotetaker;
          lastPersistedSettingsRef.current = JSON.stringify({
            ...nextSettings,
            meetingNotetaker: notetakerRest,
          });
          setMeetingNotetaker((prev) => {
            if (!prev || typeof prev !== "object" || !("consentAccepted" in prev)) {
              return prev;
            }
            const { consentAccepted: _drop, ...rest } = prev;
            return rest;
          });
        }
        // Someone changed the directory elsewhere since this window loaded, so this
        // window now shows what the save really left. With a newer edit waiting it
        // holds off: that save merges the same way and comes back here.
        if (directoryMoved && nothingNewerQueued) {
          adoptDirectory(directoryNow);
          toast.info("The Team directory changed in another window, so this one now shows the latest.");
        }
        setSettingsSaves((saves) => saves + 1);
        setSaveStatus("saved");
        saveStatusTimerRef.current = setTimeout(() => {
          setSaveStatus("idle");
        }, 1800);
      } catch (e) {
        // The code only. The thrown error carries the parsed response body,
        // and a directory refusal's body names a customer address.
        console.warn("saveData failed", e?.message);
        pendingSettingsRef.current = nextSettings;
        lastQueuedSettingsRef.current = queuedSerialized;
        // PUT /api/workspace/doc refused an address this save ADDED to the
        // Team directory (another workspace's teammate, an owner elsewhere, a
        // platform address). The WHOLE save was refused, every other setting
        // with it, and every later save carries the same address until the
        // owner removes it. Name it on the Team directory panel; a bare "Save
        // failed" left the owner guessing. NOT re-queued without the
        // directory: through the mirror above, an absent teamMembers would
        // read as "remove everyone" and revoke every teammate's access.
        const code = String(e?.data?.error || e?.message || "");
        if (code.startsWith("directory_sync_")) {
          reportDirectorySync({ ...readDirectorySyncFailure(e), scope: "doc" });
        }
        setSaveStatus("error");
      } finally {
        saveInFlightRef.current = false;
        if (
          lastQueuedSettingsRef.current &&
          lastQueuedSettingsRef.current !== queuedSerialized
        ) {
          clearTimeout(saveTimer.current);
          saveTimer.current = setTimeout(() => {
            void flushPendingSave();
          }, 250);
        }
      }
    },
    [
      canEditDirectory,
      canManageWorkspace,
      email,
      adoptDirectory,
      mirrorDirectory,
      reportDirectorySync,
      setMeetingNotetaker,
    ],
  );

  const persist = React.useCallback(
    (nextSettings) => {
      if (!canManageWorkspace || !email) return;
      try {
        localStorage.setItem(LS_KEYS.theme, nextSettings.theme);
        localStorage.setItem(LS_KEYS.projectScope, nextSettings.projectScope || "");
        localStorage.setItem(
          LS_KEYS.brainPrefs,
          JSON.stringify(nextSettings.brainPrefs || {}),
        );
        localStorage.setItem(
          LS_KEYS.aiContextFiles,
          JSON.stringify(nextSettings.aiContextFiles || []),
        );
      } catch {}
      const serialized = JSON.stringify(nextSettings);
      if (
        serialized === lastPersistedSettingsRef.current ||
        serialized === lastQueuedSettingsRef.current
      ) {
        return;
      }
      pendingSettingsRef.current = nextSettings;
      lastQueuedSettingsRef.current = serialized;
      clearTimeout(saveStatusTimerRef.current);
      setSaveStatus("saving");
      clearTimeout(saveTimer.current);
      // A save that takes the access map to NOBODY (the owner just removed
      // their last teammate, which the card's delete confirmed) goes out now,
      // not after the debounce. Closing the tab inside that window used to
      // store the empty directory through the exit flush while the "remove
      // everyone" mirror, which only runs on a live page, was never sent.
      // Only for the change that edited the directory: once a clear is owed,
      // unrelated edits (typing in a text field) keep their debounce.
      const directoryChanged =
        nextSettings?.teamMembers !== persistedDirectoryRef.current;
      persistedDirectoryRef.current = nextSettings?.teamMembers;
      const emptiesAccessMap =
        canEditDirectory &&
        directoryChanged &&
        countWritableDirectoryRows(nextSettings?.teamMembers, email) === 0 &&
        (countWritableDirectoryRows(lastMirroredDirectoryRef.current, email) > 0 ||
          clearPendingRef.current === true);
      if (emptiesAccessMap) {
        void flushPendingSave();
        return;
      }
      saveTimer.current = setTimeout(() => {
        void flushPendingSave();
      }, 350);
    },
    [canEditDirectory, canManageWorkspace, clearPendingRef, email, flushPendingSave],
  );

  const snapshot = React.useCallback(
    (overrides = {}) => ({
      automationMode,
      teamMembers,
      overdueDaysThreshold,
      riskAmountThreshold,
      theme,
      asanaConnected,
      jiraConnected,
      hubspotConnected,
      pipedriveConnected,
      linearConnected,
      clickupConnected,
      notionConnected,
      mondayConnected,
      githubConnected,
      slackConnected,
      projectScope,
      slackPrefs,
      asanaPrefs,
      jiraPrefs,
      hubspotPrefs,
      pipedrivePrefs,
      linearPrefs,
      clickupPrefs,
      notionPrefs,
      mondayPrefs,
      githubPrefs,
      teamsConnected,
      teamsPrefs,
      pollMinutes,
      automationToolPrefs,
      aiInbox,
      meetingNotetaker,
      meetingHours,
      brainPrefs,
      aiContextFiles,
      emailTrackingDomains,
      emailRetentionDays: normalizeEmailRetentionDays(emailRetentionDays),
      taskWriteBackend: overrides.taskWriteBackend ?? taskWriteBackend,
      chatWriteBackend: overrides.chatWriteBackend ?? chatWriteBackend,
      // Null for the default, never left out: the server merges the incoming
      // settings over the stored ones, so leaving the key out could never
      // put a workspace back on the default.
      calendarWriteBackend: calendarWriteBackend || null,
      // Same reason: null is sent so it is never skipped by that merge.
      pilot: pilot || null,
      // Null (the owner) is sent too, for the same reason.
      meetingCalendarEmail: meetingCalendarEmail || null,
      ...overrides,
    }),
    [
      automationMode,
      teamMembers,
      overdueDaysThreshold,
      riskAmountThreshold,
      theme,
      asanaConnected,
      jiraConnected,
      hubspotConnected,
      pipedriveConnected,
      linearConnected,
      clickupConnected,
      notionConnected,
      mondayConnected,
      githubConnected,
      slackConnected,
      slackPrefs,
      asanaPrefs,
      jiraPrefs,
      hubspotPrefs,
      pipedrivePrefs,
      linearPrefs,
      clickupPrefs,
      notionPrefs,
      mondayPrefs,
      githubPrefs,
      teamsConnected,
      automationToolPrefs,
      aiInbox,
      meetingNotetaker,
      meetingHours,
      brainPrefs,
      aiContextFiles,
      emailTrackingDomains,
      emailRetentionDays,
      teamsPrefs,
      pollMinutes,
      projectScope,
      taskWriteBackend,
      chatWriteBackend,
      calendarWriteBackend,
      pilot,
      meetingCalendarEmail,
    ],
  );

  // An older window's directory catches up when the owner comes back to it, so it does
  // not list someone removed elsewhere, or call someone added elsewhere an address
  // with stray access. Never over an edit that has not been saved yet.
  useRefreshOnReturn(
    async () => {
      const busy = () =>
        !hasHydratedSettingsRef.current || pendingSettingsRef.current || saveInFlightRef.current;
      if (busy()) return;
      const stored = await loadWorkspaceDocument(email).catch(() => null);
      const list = stored?.doc?.settings?.teamMembers;
      if (!stored?.docPresent || !Array.isArray(list) || busy()) return;
      const fresh = sanitizeTeamMembers(list);
      const known = directoryBaseOf(lastPersistedSettingsRef.current) || [];
      if (JSON.stringify(fresh) === JSON.stringify(sanitizeTeamMembers(known))) return;
      adoptDirectory(fresh);
      setDirectoryMirrorVersion((version) => version + 1);
    },
    canEditDirectory && !!email,
    { tab: null },
  );

  // Keep a mutable draft so sequential wrapped setters in the same tick
  // don't overwrite each other with stale closure state.
  const draftSettingsRef = React.useRef(null);
  React.useEffect(() => {
    draftSettingsRef.current = snapshot();
  }, [snapshot]);

  React.useEffect(() => {
    if (!canManageWorkspace || !email) return;
    const nextSettings = snapshot();
    const serialized = JSON.stringify(nextSettings);
    if (!hasHydratedSettingsRef.current) {
      hasHydratedSettingsRef.current = true;
      lastPersistedSettingsRef.current = serialized;
      // What the access map holds at the moment this page loaded: App.js
      // mirrors exactly this list on bootstrap.
      lastMirroredDirectoryRef.current = Array.isArray(nextSettings.teamMembers)
        ? nextSettings.teamMembers
        : [];
      persistedDirectoryRef.current = nextSettings.teamMembers;
      return;
    }
    if (
      serialized === lastPersistedSettingsRef.current ||
      serialized === lastQueuedSettingsRef.current
    ) {
      return;
    }
    persist(nextSettings);
  }, [canManageWorkspace, email, persist, snapshot]);

  React.useEffect(() => {
    const flushOnExit = () => {
      clearTimeout(saveTimer.current);
      if (pendingSettingsRef.current && !saveInFlightRef.current) {
        void flushPendingSave({ keepalive: true });
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushOnExit();
    };
    const onBeforeUnload = (event) => {
      if (!pendingSettingsRef.current && !saveInFlightRef.current) return undefined;
      flushOnExit();
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flushOnExit);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      clearTimeout(saveTimer.current);
      clearTimeout(saveStatusTimerRef.current);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flushOnExit);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [flushPendingSave]);

  // Leaving the Control Room for another page inside the save debounce used to
  // drop that save: the cleanup above clears the timer, App state already shows
  // the change, and the remounted page hydrates from it as "already saved".
  // Flush on UNMOUNT only (not on the dependency changes the effect above
  // re-runs for, one of which is an account switch), and without keepalive:
  // the single-page app stays alive, so the directory mirror chained after the
  // save still runs and reports into App.
  const flushPendingSaveRef = React.useRef(flushPendingSave);
  React.useEffect(() => {
    flushPendingSaveRef.current = flushPendingSave;
  }, [flushPendingSave]);
  React.useEffect(
    () => () => {
      if (pendingSettingsRef.current && !saveInFlightRef.current) {
        void flushPendingSaveRef.current();
      }
    },
    [],
  );

  function wrapState(setter, key) {
    return (valueOrUpdater) => {
      setter((prev) => {
        const nextVal =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;
        const base = draftSettingsRef.current || snapshot();
        const nextSettings = { ...base, [key]: nextVal };
        draftSettingsRef.current = nextSettings;
        return nextVal;
      });
    };
  }
  const setProjectScopeWrapped = wrapState(setProjectScope, "projectScope");
  const setAutomationModeWrapped = wrapState(
    setAutomationMode,
    "automationMode",
  );
  const setTeamMembersWrapped = wrapState(setTeamMembers, "teamMembers");
  const setOverdueWrapped = wrapState(
    setOverdueDaysThreshold,
    "overdueDaysThreshold",
  );
  const setRiskWrapped = wrapState(
    setRiskAmountThreshold,
    "riskAmountThreshold",
  );
  const setAsanaWrapped = wrapState(setAsanaConnected, "asanaConnected");
  const setJiraWrapped = wrapState(setJiraConnected, "jiraConnected");
  const setHubspotWrapped = wrapState(
    setHubspotConnected,
    "hubspotConnected",
  );
  const setPipedriveWrapped = wrapState(
    setPipedriveConnected,
    "pipedriveConnected",
  );
  const setSlackConnectedWrapped = wrapState(
    setSlackConnected,
    "slackConnected",
  );
  const setSlackPrefsWrapped = wrapState(setSlackPrefs, "slackPrefs");
  const setAsanaPrefsWrapped = wrapState(setAsanaPrefs, "asanaPrefs");
  const setJiraPrefsWrapped = wrapState(setJiraPrefs, "jiraPrefs");
  const setHubspotPrefsWrapped = wrapState(setHubspotPrefs, "hubspotPrefs");
  const setPipedrivePrefsWrapped = wrapState(setPipedrivePrefs, "pipedrivePrefs");
  const setLinearWrapped = wrapState(setLinearConnected, "linearConnected");
  const setLinearPrefsWrapped = wrapState(setLinearPrefs, "linearPrefs");
  const setClickupWrapped = wrapState(setClickupConnected, "clickupConnected");
  const setClickupPrefsWrapped = wrapState(setClickupPrefs, "clickupPrefs");
  const setNotionWrapped = wrapState(setNotionConnected, "notionConnected");
  const setNotionPrefsWrapped = wrapState(setNotionPrefs, "notionPrefs");
  const setMondayWrapped = wrapState(setMondayConnected, "mondayConnected");
  const setMondayPrefsWrapped = wrapState(setMondayPrefs, "mondayPrefs");
  const setGithubWrapped = wrapState(setGithubConnected, "githubConnected");
  const setGithubPrefsWrapped = wrapState(setGithubPrefs, "githubPrefs");
  const setTeamsConnectedWrapped = wrapState(
    setTeamsConnected,
    "teamsConnected",
  );
  const setTeamsPrefsWrapped = wrapState(setTeamsPrefs, "teamsPrefs");
  const setTaskWriteBackendWrapped = wrapState(
    setTaskWriteBackend,
    "taskWriteBackend",
  );
  const setCalendarWriteBackendWrapped = wrapState(
    typeof setCalendarWriteBackend === "function"
      ? setCalendarWriteBackend
      : () => {},
    "calendarWriteBackend",
  );
  const setMeetingCalendarEmailWrapped = wrapState(
    typeof setMeetingCalendarEmail === "function" ? setMeetingCalendarEmail : () => {},
    "meetingCalendarEmail",
  );
  const setPollMinutesWrapped = wrapState(setPollMinutes, "pollMinutes");
  const setAutomationToolPrefsWrapped = wrapState(
    setAutomationToolPrefs,
    "automationToolPrefs",
  );
  // The task card's choice. Auto-create & update tasks is the only automation
  // that creates tasks, so it writes the workspace's task tool (which the
  // planner prompt and the executor read) and drops the old per-automation
  // override (automationToolPrefs.autoTasksBackend), which the worker would
  // otherwise still honour ahead of it.
  const chooseTaskTool = (value) => {
    setTaskWriteBackendWrapped(value);
    setAutomationToolPrefsWrapped((prev) => withoutTaskOverride(prev));
  };
  const setAiInboxWrapped = wrapState(setAiInbox, "aiInbox");
  const setMeetingNotetakerWrapped = wrapState(
    setMeetingNotetaker,
    "meetingNotetaker",
  );
  const setBrainPrefsWrapped = wrapState(setBrainPrefs, "brainPrefs");
  const setAiContextFilesWrapped = wrapState(setAiContextFiles, "aiContextFiles");
  const setEmailTrackingDomainsWrapped = wrapState(setEmailTrackingDomains, "emailTrackingDomains");
  const setEmailRetentionDaysWrapped = wrapState(
    typeof setEmailRetentionDays === "function" ? setEmailRetentionDays : () => {},
    "emailRetentionDays",
  );
  const automationModeEffective = automationMode === "auto" ? "auto" : "manual";
  const executionModes = React.useMemo(
    () =>
      automationToolPrefs?.executionModes &&
      typeof automationToolPrefs.executionModes === "object"
        ? automationToolPrefs.executionModes
        : {},
    [automationToolPrefs],
  );
  const executionModeFor = React.useCallback(
    (purpose) =>
      executionModes?.[purpose] === "approval" ? "approval" : "auto",
    [executionModes],
  );
  const setExecutionMode = React.useCallback(
    (purpose, mode) => {
      const key = String(purpose || "").trim();
      if (!key) return;
      setAutomationToolPrefsWrapped((prev) => {
        const base = prev && typeof prev === "object" ? prev : {};
        const prevModes =
          base.executionModes && typeof base.executionModes === "object"
            ? base.executionModes
            : {};
        const nextModes = { ...prevModes };
        if (mode === "approval") nextModes[key] = "approval";
        else delete nextModes[key];
        return { ...base, executionModes: nextModes };
      });
    },
    [setAutomationToolPrefsWrapped],
  );
  const executionModeOptions = React.useMemo(
    () => [
      { value: "auto", label: "Run automatically" },
      { value: "approval", label: "Require approval" },
    ],
    [],
  );
  function renderExecutionModeControl(purpose, label = "Write mode") {
    if (automationModeEffective !== "auto") {
      return (
        <div className="muted small">Approve each action in Audit Logs.</div>
      );
    }
    return (
      <AutomationToolPicker
        label={label}
        value={executionModeFor(purpose)}
        onChange={(v) => setExecutionMode(purpose, v)}
        options={executionModeOptions}
      />
    );
  }

  // Inbox status. Read-only: everything here comes from GET /api/ai-inbox/status,
  // scoped to this workspace only, and nothing on it writes. It sits in the Email
  // assistant card, outside the admin fieldset, so a member can still refresh it.
  function renderInboxStatus() {
    return (
      <div className="crSubGroup" data-testid="inbox-status">
        <div className="crCardHead">
          <div className="crFieldLabel">Inbox status</div>
          <div className="crCardHead__aside">
            <button
              type="button"
              className="btn ghost"
              onClick={refreshAiInboxStatus}
              disabled={aiInboxStatusState === "loading"}
            >
              {aiInboxStatusState === "loading" ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
        {aiInboxStatusState === "error" || !aiInboxStatus ? (
          <div className="muted small" style={{ marginTop: 8 }}>
            {aiInboxStatusState === "loading"
              ? "Loading inbox status…"
              : "Couldn't load inbox status."}
          </div>
        ) : (
          <div
            style={{ display: "grid", gap: 6, marginTop: 10 }}
          >
            <div className="muted small">
              <strong>Address</strong> — {aiInboxStatus.address}. Shared by every
              BossDrop workspace.
            </div>
            <div className="muted small">
              <strong>Mail check</strong> —{" "}
              {!aiInboxStatus.poller?.running
                ? "Not running on this server."
                : aiInboxStatus.poller?.lastPollOk === false
                  ? "Running, but the last check failed."
                  : aiInboxStatus.poller?.lastPollAt
                    ? `Running, last checked ${timeAgoLabel(aiInboxStatus.poller.lastPollAt)}.`
                    : "Running, no check completed yet."}
            </div>
            <div className="muted small">
              <strong>Sender verification</strong> —{" "}
              {aiInboxStatus.poller?.senderVerification === "monitor_only"
                ? "Monitor only: nothing is blocked."
                : aiInboxStatus.poller?.senderVerification === "lenient"
                  ? "Lenient: accepts unverified senders."
                  : "Verified senders only."}
            </div>
            <div className="muted small">
              <strong>Last message received</strong> —{" "}
              {aiInboxStatus.lastInboundAt
                ? `${dateTimeLabel(aiInboxStatus.lastInboundAt)}${
                    aiInboxStatus.lastInboundFrom
                      ? ` from ${aiInboxStatus.lastInboundFrom}`
                      : ""
                  }`
                : "Nothing yet."}
            </div>
            <div className="muted small">
              <strong>Last reply sent</strong> —{" "}
              {aiInboxStatus.lastReplyAt ? (
                dateTimeLabel(aiInboxStatus.lastReplyAt)
              ) : (
                <>
                  — (only recorded from this release onwards, so a reply sent
                  before it does not show here)
                </>
              )}
            </div>
            <div className="muted small">
              <strong>Replies sent today</strong> —{" "}
              {aiInboxStatus.capReached
                ? `${aiInboxStatus.repliesSentToday} of ${aiInboxStatus.dailyReplyCap} — limit reached, replies paused until midnight ${aiInboxStatus.tz}.`
                : `${aiInboxStatus.repliesSentToday} of ${aiInboxStatus.dailyReplyCap}.`}
            </div>
            {(aiInboxStatus.drops || []).length ? (
              <div className="muted small">
                <strong>Dropped today</strong> —{" "}
                {(aiInboxStatus.drops || []).reduce(
                  (sum, d) => sum + (Number(d.count) || 0),
                  0,
                )}
                :
                <ul style={{ margin: "4px 0 0", paddingInlineStart: 18 }}>
                  {(aiInboxStatus.drops || []).map((d) => (
                    <li key={d.reason}>
                      {AI_INBOX_DROP_LABELS[d.reason] || d.reason} — {d.count}
                      {dropDetailSuffix(d.reason, d.lastDetail)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="muted small">
                <strong>Dropped today</strong> — none.
              </div>
            )}
            <div className="muted small" style={{ marginTop: 4 }}>
              Mail from an address this workspace doesn't recognise is dropped
              before BossDrop knows which workspace it was meant for, so it isn't
              counted here. If someone's email is going unanswered, add them to
              Team directory.
            </div>
            {aiInboxStatus.poller?.configWarning ? (
              <div className="muted small" style={{ marginTop: 4 }}>
                {/* The warning already states its own consequence, which
                    differs by verification mode (enforce drops, lenient
                    accepts unverified). Appending one here contradicted the
                    "Lenient" line directly above it. */}
                <span className={aiInboxWarningChip(aiInboxStatus.poller).className}>
                  {aiInboxWarningChip(aiInboxStatus.poller).label}
                </span>{" "}
                {aiInboxStatus.poller.configWarning}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  function renderAiInboxControls(globalAuto) {
    if (!inbox.enabled) {
      return (
        <div className="muted small">
          Off. Mail to {inboxAddress} is ignored — not stored, not read, not
          answered.
        </div>
      );
    }

    const senderHelp =
      inbox.senderPolicy === "roster_only"
        ? "Team directory and you. Strangers are always ignored."
        : inbox.senderPolicy === "thread_only"
          ? // Deliberately "an existing BossDrop thread", not "mail it sent":
            // the match is on any message already stored for this workspace,
            // which includes an inbound one BossDrop merely filed.
            "Only replies to a thread BossDrop already has on file."
          : "Team directory, hub members, and you. Strangers are always ignored.";

    // A reply to a team member who wrote in follows the reply mode alone, in
    // manual mode too (server/utils/requestApprovalPolicy.js "answers_sender");
    // manual mode still holds a reply to anyone else.
    const replyHelp =
      inbox.replyMode === "off"
        ? "Reads and files the email, never answers it."
        : inbox.replyMode === "approval"
          ? globalAuto
            ? "Replies wait for you; the rest of the workspace still runs itself."
            : "Replies wait for you."
          : globalAuto
            ? "Replies send without you."
            : "Answers your team right away. In manual mode, a reply to anyone else waits for you.";

    return (
      <div className="crFieldRow">
        <AutomationToolPicker
          label="Who may email BossDrop"
          value={inbox.senderPolicy}
          onChange={(v) => setInboxKey("senderPolicy", v)}
          options={[
            { value: "known", label: "Anyone this workspace recognises" },
            { value: "roster_only", label: "Team directory only" },
            {
              value: "thread_only",
              label: "Only replies inside an existing BossDrop thread",
            },
          ]}
          help={senderHelp}
        />

        <AutomationToolPicker
          label="What BossDrop does with a message"
          value={inbox.replyMode}
          onChange={(v) => setInboxKey("replyMode", v)}
          options={[
            { value: "auto", label: "Write and send the reply" },
            { value: "approval", label: "Write the reply, wait for my approval" },
            { value: "off", label: "Never reply" },
          ]}
          help={replyHelp}
        />

        <AutomationToolPicker
          label="What a message can set off"
          value={inbox.onInbound}
          onChange={(v) => setInboxKey("onInbound", v)}
          options={[
            { value: "reply_only", label: "Reply only" },
            { value: "full_run", label: "Reply and act" },
          ]}
          help={
            inbox.onInbound === "reply_only"
              ? "Answers and stops. Cheaper: one decision per message."
              : "Can also create tasks, escalate or book a meeting."
          }
        />

        <label className="crField">
          <span className="crFieldLabel">Daily reply limit</span>
          <input
            type="number"
            className="inputFancy"
            min={1}
            max={500}
            step={1}
            value={
              replyCapDraft === null ? String(inbox.dailyReplyCap) : replyCapDraft
            }
            onChange={(e) => {
              const raw = e.target.value;
              setReplyCapDraft(raw);
              // Save only a value that is already complete and in range. An
              // empty or half-typed field is held until blur rather than
              // round-tripped through the save as 1.
              const n = Number(raw);
              if (
                raw.trim() !== "" &&
                Number.isFinite(n) &&
                Number.isInteger(n) &&
                n >= 1 &&
                n <= 500
              ) {
                setInboxKey("dailyReplyCap", n);
              }
            }}
            onBlur={() => {
              if (replyCapDraft === null) return;
              const n = Number(replyCapDraft);
              // Nothing usable typed -> keep what was already saved.
              if (replyCapDraft.trim() !== "" && Number.isFinite(n)) {
                setInboxKey(
                  "dailyReplyCap",
                  Math.min(500, Math.max(1, Math.floor(n))),
                );
              }
              setReplyCapDraft(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
          />
          <span className="muted small">
            Guards against reply loops. Mail still arrives; answers resume at
            midnight {inboxTzLabel}.
          </span>
        </label>

        <label className="crField">
          <span className="crFieldLabel">Sign-off name</span>
          <input
            type="text"
            className="inputFancy"
            maxLength={60}
            placeholder="BossDrop"
            value={inbox.signOffName}
            onChange={(e) => setInboxKey("signOffName", e.target.value)}
          />
          <span className="muted small">How BossDrop signs its replies.</span>
        </label>

        <label className="crField">
          <span className="crFieldLabel">Signature line</span>
          <input
            type="text"
            className="inputFancy"
            maxLength={200}
            placeholder="— BossDrop, for the Acme ops team"
            value={inbox.signatureLine}
            onChange={(e) => setInboxKey("signatureLine", e.target.value)}
          />
          <span className="muted small">
            Added to the bottom of every reply, exactly as you type it.
          </span>
        </label>

        {/* One tone for everything BossDrop writes (backend
            conversations/voice.js), not only email replies (Oct 29 2026). */}
        <AutomationToolPicker
          label="Tone (chat and email)"
          value={inbox.replyTone}
          onChange={(v) => setInboxKey("replyTone", v)}
          options={[
            { value: "professional", label: "Professional" },
            { value: "friendly", label: "Friendly" },
            { value: "direct", label: "Direct" },
            { value: "formal", label: "Formal" },
          ]}
          help="How BossDrop sounds everywhere it writes: chat replies, direct messages, escalations and email."
        />
      </div>
    );
  }

  // ---- AI inbox (ai@bossdrop.ai) ----
  // Mirrors normalizeAiInboxSettings() in server/utils/aiInboxPolicy.js so the
  // card renders what the backend will actually enforce. The backend clamps
  // again on save; this is only so the controls never render against undefined.
  const inbox = React.useMemo(
    () => ({
      enabled: aiInbox?.enabled !== false,
      senderPolicy: aiInbox?.senderPolicy || "known",
      replyMode: aiInbox?.replyMode || "auto",
      onInbound: aiInbox?.onInbound || "full_run",
      // Same three steps as the backend: "" / null / undefined mean ABSENT
      // (Number("") and Number(null) are both 0, which would render "Daily
      // reply limit: 0" for a limit the backend reads as 50), then floor, then
      // clamp to [1, 500] so the chip can never name a cap that will not be
      // enforced.
      dailyReplyCap: (() => {
        const raw = aiInbox?.dailyReplyCap;
        const n =
          raw === "" || raw === null || raw === undefined
            ? NaN
            : Math.floor(Number(raw));
        return Number.isFinite(n) ? Math.min(500, Math.max(1, n)) : 50;
      })(),
      signOffName: aiInbox?.signOffName || "",
      signatureLine: aiInbox?.signatureLine || "",
      replyTone: aiInbox?.replyTone || "professional",
    }),
    [aiInbox],
  );
  const setInboxKey = React.useCallback(
    (key, value) =>
      setAiInboxWrapped((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        [key]: value,
      })),
    [setAiInboxWrapped],
  );
  // Read-only facts about the shared mailbox, with safe fallbacks so the card
  // still renders when GET /api/ai-inbox/status hasn't answered (or failed).
  const inboxAddress = String(aiInboxStatus?.address || "ai@bossdrop.ai");
  const inboxPollMinutes =
    Number(aiInboxStatus?.poller?.intervalMinutes) > 0
      ? Number(aiInboxStatus.poller.intervalMinutes)
      : 2;
  const inboxRepliesToday = Number.isFinite(
    Number(aiInboxStatus?.repliesSentToday),
  )
    ? Number(aiInboxStatus.repliesSentToday)
    : null;
  // Same candidate order as resolveAiInboxTimeZone() in
  // server/utils/aiInboxPolicy.js, as far as this page can see it: slackPrefs.tz
  // FIRST, then businessHours.tz. (Its settings.tz / settings.businessHours.tz
  // steps read fields this page's context does not carry, the same gap
  // resolvePollWindow above documents.) So the zone this copy names is the zone
  // the cap actually resets on even before GET /api/ai-inbox/status answers. No
  // browser-zone guess -- the backend never looks at the reader's machine, and
  // guessing named a midnight nothing resets at.
  const inboxTzLabel = String(
    aiInboxStatus?.tz ||
      slackPrefs?.tz ||
      slackPrefs?.businessHours?.tz ||
      "Etc/UTC",
  );

  const confidencePrefs = React.useMemo(
    () => ({
      ...BRAIN_CONF_DEFAULTS,
      ...(brainPrefs && typeof brainPrefs === "object" ? brainPrefs : {}),
    }),
    [brainPrefs],
  );
  const currentAIProvider = React.useMemo(() => {
    const raw = String(brainPrefs?.aiProvider || "claude")
      .trim()
      .toLowerCase();
    return AI_PROVIDER_OPTIONS.find((option) => option.value === raw)?.value || "claude";
  }, [brainPrefs]);
  const currentAIProviderMeta = React.useMemo(
    () =>
      AI_PROVIDER_OPTIONS.find((option) => option.value === currentAIProvider) ||
      AI_PROVIDER_OPTIONS[0],
    [currentAIProvider],
  );
  // Selectable models come from the backend catalog (backend/utils/modelCatalog.js)
  // so the list shown here, the defaults, and server-side validation cannot drift.
  const [aiModelCatalog, setAiModelCatalog] = React.useState(null);
  const [aiModelCatalogFailed, setAiModelCatalogFailed] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    apiRequest("/api/ai/models")
      .then((json) => {
        if (cancelled) return;
        if (json?.ok && Array.isArray(json.providers)) setAiModelCatalog(json);
        else setAiModelCatalogFailed(true);
      })
      .catch(() => {
        if (!cancelled) setAiModelCatalogFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const catalogForProvider = React.useCallback(
    (provider) =>
      (aiModelCatalog?.providers || []).find((p) => p.value === provider) || null,
    [aiModelCatalog],
  );
  const modelLabelFor = (providerCatalog, id) =>
    providerCatalog?.models?.find((m) => m.id === id)?.label || id || "";
  const currentProviderCatalog = catalogForProvider(currentAIProvider);
  // A stored id the catalog no longer offers (retired, other provider) shows as
  // the default, which is also what the backend falls back to for it.
  const knownModelOrEmpty = (id) =>
    currentProviderCatalog?.models?.some((m) => m.id === id) ? id : "";
  const planningModelValue = knownModelOrEmpty(brainPrefs?.planningModel);
  const criticModelValue = knownModelOrEmpty(brainPrefs?.criticModel);
  const modelChoices = (currentProviderCatalog?.models || []).map((m) => ({
    value: m.id,
    label: `${m.label}${m.recommended ? " (recommended)" : ""} · ${m.tier}`,
  }));
  const planningModelOptions = [
    {
      value: "",
      label: `BossDrop default (${modelLabelFor(
        currentProviderCatalog,
        currentProviderCatalog?.defaultModel,
      )})`,
    },
    ...modelChoices,
  ];
  const criticModelOptions = [
    { value: "", label: "Same as main model" },
    ...modelChoices,
  ];
  const activeModelLabel = currentProviderCatalog
    ? modelLabelFor(
        currentProviderCatalog,
        planningModelValue || currentProviderCatalog.defaultModel,
      )
    : currentAIProviderMeta.model;
  const setBrainModel = (slotKey, value) =>
    setBrainPrefsWrapped((prev) => ({
      ...(prev && typeof prev === "object" ? prev : {}),
      [slotKey]: value,
    }));
  const contextFilesSafe = React.useMemo(
    () => (Array.isArray(aiContextFiles) ? aiContextFiles : []),
    [aiContextFiles],
  );
  const handleOpenContextFilePicker = React.useCallback(() => {
    if (contextFilesBusy || !canManageWorkspace) return;
    contextFileInputRef.current?.click?.();
  }, [canManageWorkspace, contextFilesBusy]);
  const handleContextFilesSelected = React.useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length || !canManageWorkspace) return;

      const existingCount = Array.isArray(draftSettingsRef.current?.aiContextFiles)
        ? draftSettingsRef.current.aiContextFiles.length
        : contextFilesSafe.length;
      if (existingCount >= MAX_AI_CONTEXT_FILES) {
        setContextFilesError(
          `You can keep up to ${MAX_AI_CONTEXT_FILES} AI context files at once.`,
        );
        return;
      }

      setContextFilesBusy(true);
      setContextFilesError("");
      setContextFilesStatus("");
      const prepared = [];

      try {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          setContextFilesStatus(
            `Processing ${file.name} (${i + 1}/${files.length})...`,
          );
          const nextEntry = await prepareWorkspaceContextFile(file);
          if (nextEntry) prepared.push(nextEntry);
          const nextCount = existingCount + prepared.length;
          if (nextCount >= MAX_AI_CONTEXT_FILES) break;
        }

        if (prepared.length) {
          setAiContextFilesWrapped((prev) => mergeContextFiles(prev, prepared));
          setContextFilesStatus(
            prepared.length === 1
              ? `${prepared[0].name} added to AI context.`
              : `${prepared.length} files added to AI context.`,
          );
        } else {
          setContextFilesStatus("");
        }
      } catch (error) {
        setContextFilesError(
          String(error?.message || error || "context_file_prepare_failed"),
        );
      } finally {
        setContextFilesBusy(false);
        if (contextFileInputRef.current) {
          contextFileInputRef.current.value = "";
        }
      }
    },
    [
      canManageWorkspace,
      contextFilesSafe.length,
      setAiContextFilesWrapped,
    ],
  );
  const handleRemoveContextFile = React.useCallback(
    (fileId) => {
      setContextFilesError("");
      setContextFilesStatus("");
      setAiContextFilesWrapped((prev) =>
        (Array.isArray(prev) ? prev : []).filter((entry) => entry?.id !== fileId),
      );
    },
    [setAiContextFilesWrapped],
  );
  const confidenceControls = React.useMemo(
    () => [
      {
        key: "autoExecuteConfidence",
        label: "General action confidence",
        help: "Baseline confidence required before most actions are auto-executed.",
      },
      {
        key: "askClarifyConfidence",
        label: "Clarify reroute confidence",
        help: "Lower-confidence actions above this value are rerouted to clarifying questions.",
      },
      {
        key: "minMeetingConfidence",
        label: "Meeting minimum confidence",
        help: "Minimum confidence required to auto-schedule a meeting.",
      },
      {
        key: "minEscalationConfidence",
        label: "Escalation minimum confidence",
        help: "Minimum confidence required before sending an escalation.",
      },
    ],
    [],
  );
  const getBrainConfidencePct = React.useCallback(
    (key) => {
      const raw = Number(confidencePrefs?.[key]);
      const clamped = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0;
      return Math.round(clamped * 100);
    },
    [confidencePrefs],
  );
  const setBrainConfidencePct = React.useCallback(
    (key, value) => {
      if (!canManageWorkspace) return;
      const pct = Math.max(0, Math.min(100, Number(value) || 0));
      const next01 = pct / 100;
      setBrainPrefsWrapped((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        [key]: next01,
      }));
    },
    [canManageWorkspace, setBrainPrefsWrapped],
  );
  const resetBrainConfidenceDefaults = React.useCallback(() => {
    setBrainPrefsWrapped((prev) => ({
      ...(prev && typeof prev === "object" ? prev : {}),
      ...BRAIN_CONF_DEFAULTS,
    }));
  }, [setBrainPrefsWrapped]);

  const aggressionLevel = React.useMemo(() => {
    const raw = Number(brainPrefs?.aggressionLevel);
    return Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 50;
  }, [brainPrefs]);
  const setAggressionLevel = React.useCallback(
    (val) => {
      if (!canManageWorkspace) return;
      // 0 is a level (the most cautious), not "unset": `Number(val) || 50`
      // snapped the dial from 0 back to 50 (Oct 29 2026).
      const n = val === "" || val == null ? NaN : Number(val);
      const clamped = Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 50;
      setBrainPrefsWrapped((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        aggressionLevel: clamped,
      }));
    },
    [canManageWorkspace, setBrainPrefsWrapped],
  );
  // Mirrors computeAggressionBrainPrefs() in server/brain/decisionBrain.js so the
  // dial shows what it actually resolves to instead of an unverifiable adjective.
  // Keep these two in sync if the backend curve changes.
  const autonomyPreview = React.useMemo(() => {
    const a = Math.max(0, Math.min(100, Number(aggressionLevel) || 0));
    const t = a / 100;
    const lerp = (lo, hi, frac) => lo + (hi - lo) * frac;
    const fpTtlHours =
      a <= 50
        ? Math.round(lerp(168, 48, a / 50))
        : Math.round(lerp(48, 8, (a - 50) / 50));
    const repeatWindow =
      fpTtlHours >= 48
        ? `${Math.round(fpTtlHours / 24)} days`
        : `${fpTtlHours} hours`;
    let label = "Balanced";
    if (a <= 20) label = "Cautious";
    else if (a <= 40) label = "Measured";
    else if (a <= 70) label = "Balanced";
    else if (a <= 90) label = "Proactive";
    else label = "Aggressive";
    return {
      label,
      autoExecuteConfidence: lerp(0.9, 0.45, t),
      maxActionsPerTick: Math.round(lerp(3, 20, t)),
      fpTtlHours,
      repeatWindow,
    };
  }, [aggressionLevel]);
  // Read-only inbox status. Everything it renders is derived server-side from
  // this workspace alone (GET /api/ai-inbox/status), so members see it too.
  const refreshAiInboxStatus = React.useCallback(async () => {
    setAiInboxStatusState("loading");
    try {
      const base = String(apiBase || "").replace(/\/+$/, "");
      const response = await fetch(`${base}/api/ai-inbox/status`, {
        credentials: "include",
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || "ai_inbox_status_failed");
      setAiInboxStatus(json);
      setAiInboxStatusState("ready");
    } catch (error) {
      console.warn("ai inbox status failed", error);
      setAiInboxStatus(null);
      setAiInboxStatusState("error");
    }
  }, [apiBase]);
  React.useEffect(() => {
    refreshAiInboxStatus();
  }, [refreshAiInboxStatus]);

  const saveStatusMeta = React.useMemo(() => {
    if (!canManageWorkspace) return null;
    if (saveStatus === "saving") {
      return { label: "Saving changes…", tone: "warn" };
    }
    if (saveStatus === "saved") {
      return { label: "All changes saved", tone: "ok" };
    }
    if (saveStatus === "error") {
      // The Team directory banner names the address that blocked it.
      if (directorySyncError?.scope === "doc") {
        return { label: "Save blocked by the Team directory", tone: "bad" };
      }
      return { label: "Save failed", tone: "bad" };
    }
    return null;
  }, [canManageWorkspace, directorySyncError, saveStatus]);
  // Observed by SaveToast: the toast only shows once this header (and the save
  // chip inside it) has scrolled out of view.
  const headerRef = React.useRef(null);
  /* ---------- schedule state (mirrors slackPrefs.reportCron) ---------- */
  const tzDefault = React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC",
    [],
  );
  const schedParsed = React.useMemo(
    () => parseReportSchedule(slackPrefs?.reportCron),
    [slackPrefs?.reportCron],
  );

  const [reportFreq, setReportFreq] = React.useState(
    schedParsed?.freq || "weekly",
  );
  const [day, setDay] = React.useState(schedParsed?.day || "FRI"); // only used for weekly
  const [time, setTime] = React.useState(schedParsed?.time || "09:00");
  const [tz, setTz] = React.useState(schedParsed?.tz || tzDefault);

  React.useEffect(() => {
    if (!schedParsed) return;
    setReportFreq(schedParsed.freq || "weekly");
    if (schedParsed.day) setDay(schedParsed.day);
    setTime(schedParsed.time);
    setTz(schedParsed.tz);
  }, [
    schedParsed?.freq,
    schedParsed?.day,
    schedParsed?.time,
    schedParsed?.tz,
  ]);

  const reportPreview = buildReportSchedule({
    freq: reportFreq,
    day,
    time,
    tz,
  });

  /*   React.useEffect(() => {
    if (!cronParsed) return;
    setDay(cronParsed.day);
    setTime(cronParsed.time);
    setTz(cronParsed.tz);
  }, [cronParsed?.day, cronParsed?.time, cronParsed?.tz]); */

  const cronPreview = buildCronTZ({ day, time, tz });
  const tzOptions = React.useMemo(() => getSupportedTimeZones(), []);
  // current business hours snapshot (with sane defaults)
  const bh = React.useMemo(() => {
    const b = slackPrefs?.businessHours || {};
    return {
      tz: b.tz || Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC",
      start: b.start || "09:00",
      end: b.end || "17:30",
      days: Array.isArray(b.days) && b.days.length ? b.days : BH_DEFAULT_DAYS,
    };
  }, [slackPrefs?.businessHours]);

  // Meetings book inside business hours, so hours that cover nights or
  // weekends get a warning under the card.
  const bhRoundTheClock = React.useMemo(() => {
    const raw = slackPrefs?.businessHours;
    if (!raw || !raw.start || !raw.end) return false;
    const span = toMin(raw.end) - toMin(raw.start);
    const days = (Array.isArray(raw.days) ? raw.days : []).map((d) =>
      String(d).toUpperCase(),
    );
    return span >= 14 * 60 || days.includes("SAT") || days.includes("SUN") ||
      days.includes("6") || days.includes("0");
  }, [slackPrefs?.businessHours]);

  // ---- business-hours-only polling (settings.slackPrefs.pollDuringBusinessHoursOnly) ----
  const pollBusinessHoursOnly = !!slackPrefs?.pollDuringBusinessHoursOnly;
  // Validated against the RAW stored value, exactly what the worker will read.
  const pollWindow = React.useMemo(
    () => resolvePollWindow(slackPrefs),
    [slackPrefs],
  );
  const bhUsableForPolling = pollWindow.ok;
  // Only "active" when the backend will actually honour it -- it fails open on
  // unusable hours, so a bad config must not show a reduced tick count.
  const pollGateActive = pollBusinessHoursOnly && bhUsableForPolling;
  // Teammate timezones widen the gate (see pollWeekCoverage): the worker stays
  // awake for each teammate's own working day so their daily status ping can
  // still be delivered.
  const memberZones = React.useMemo(
    () =>
      Array.from(
        new Set(
          (teamMembers || [])
            .map((m) => (typeof m?.tz === "string" ? m.tz.trim() : ""))
            .filter((tz) => tz && isUsableTz(tz)),
        ),
      ),
    [teamMembers],
  );
  const pollCoverage = React.useMemo(
    () =>
      pollGateActive
        ? pollWeekCoverage(pollWindow, memberZones)
        : { minutes: 0, zones: [], disjointZones: [] },
    [pollGateActive, pollWindow, memberZones],
  );
  const ticksAlways = pollTicksPerWeek(pollMinutesSafe, null);
  const ticksEffective = pollGateActive
    ? Math.min(
        ticksAlways,
        Math.round(pollCoverage.minutes / Math.max(1, pollMinutesSafe)),
      )
    : ticksAlways;
  const extraPollZones = pollGateActive ? pollCoverage.zones.slice(1) : [];
  const ticksSavedPct = ticksAlways
    ? Math.max(0, Math.round((1 - ticksEffective / ticksAlways) * 100))
    : 0;
  // Every 5 minutes around the clock -- the yardstick for "is this still expensive?".
  const baselineTicksPerWeek = pollTicksPerWeek(5, null); // 2016
  // Describes the window the WORKER resolved, not the defaulted editor copy, so
  // the sentence under the toggle can never name hours the gate is not using.
  const pollWindowLabel = pollWindow.ok
    ? `${BH_DAY_TOKENS.filter((d) => pollWindow.days.includes(d)).join(", ")} ${
        pollWindow.start
      }–${pollWindow.end} ${pollWindow.tz}`
    : "";

  return (
    <section className="panel controlRoom">
      <SaveToast
        status={canManageWorkspace ? saveStatus : "idle"}
        anchorRef={headerRef}
      />
      <div
        ref={headerRef}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>Control Room</h2>
        {saveStatusMeta ? (
          <span className={`chip ${saveStatusMeta.tone}`}>
            {saveStatusMeta.label}
          </span>
        ) : null}
      </div>
      {/* `canManageWorkspaceAccess`, not `canManageWorkspace`: this banner is
          about your ACCESS LEVEL. The other reason this page goes read-only --
          the workspace document could not be read -- is a different sentence
          and the layout says it above, so an admin during an outage is not
          told their access was downgraded. */}
      {!canManageWorkspaceAccess ? (
        <div className="infoBanner" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Read-only mode</div>
          <div className="muted small">
            Your access level is <strong>{workspaceSecurityLevel || "member"}</strong>.
            You can review the workspace and connect your own calendar accounts
            from your teammate card, but only admins can change hub settings.
          </div>
        </div>
      ) : null}
      {/* No BossDrop-admin block here on purpose. Creating a new CLIENT
          COMPANY (tier 1, platform admins only) lives on its own page,
          pages/ClientAccounts.js at /clients, so that BossDrop's own admin
          work is never on the same screen as a client's workspace settings.
          The other invite path, bringing a person into THIS workspace, is
          tier 2 and sits in Team Directory below, open to any workspace
          owner or admin. */}
      <div className="crTabs" role="tablist" aria-label="Control Room">
        {CONTROL_ROOM_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`cr-tab-${t.key}`}
            aria-controls={`cr-panel-${t.key}`}
            aria-selected={section === t.key}
            className={`crTab${section === t.key ? " isActive" : ""}`}
            onClick={() => chooseSection(t.key)}
          >
            <CrIcon name={t.icon} />
            {t.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id="cr-panel-context"
        aria-labelledby="cr-tab-context"
        hidden={section !== "context"}
      >
      <fieldset
        disabled={!canManageWorkspace}
        style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
      >
      {/* The AI inbox is not a connector — the address is server-owned and
          always there — but people look for it here, so point at where it
          actually lives. */}
      <div className="muted small" style={{ margin: "12px 0 0" }}>
        BossDrop also has its own email address — set it up under{" "}
        <strong>Automations → Email assistant</strong>.
      </div>
      {/* Connectors (uploads) — title lives inside ConnectorsSection's own card */}
      <ConnectorsSection
        asanaConnected={asanaConnected}
        setAsanaConnected={setAsanaWrapped}
        setAsanaData={setAsanaData}
        jiraConnected={jiraConnected}
        setJiraConnected={setJiraWrapped}
        setJiraData={setJiraData}
        hubspotConnected={hubspotConnected}
        setHubspotConnected={setHubspotWrapped}
        pipedriveConnected={pipedriveConnected}
        setPipedriveConnected={setPipedriveWrapped}
        slackConnected={slackConnected}
        setSlackConnected={setSlackConnectedWrapped}
        slackPrefs={slackPrefs}
        setAsanaPrefs={setAsanaPrefsWrapped}
        setJiraPrefs={setJiraPrefsWrapped}
        hubspotPrefs={hubspotPrefs}
        setHubspotPrefs={setHubspotPrefsWrapped}
        pipedrivePrefs={pipedrivePrefs}
        setPipedrivePrefs={setPipedrivePrefsWrapped}
        linearConnected={linearConnected}
        setLinearConnected={setLinearWrapped}
        setLinearData={setLinearData}
        linearPrefs={linearPrefs}
        setLinearPrefs={setLinearPrefsWrapped}
        clickupConnected={clickupConnected}
        setClickupConnected={setClickupWrapped}
        setClickupData={setClickupData}
        clickupPrefs={clickupPrefs}
        setClickupPrefs={setClickupPrefsWrapped}
        notionConnected={notionConnected}
        setNotionConnected={setNotionWrapped}
        setNotionData={setNotionData}
        notionPrefs={notionPrefs}
        setNotionPrefs={setNotionPrefsWrapped}
        mondayConnected={mondayConnected}
        setMondayConnected={setMondayWrapped}
        setMondayData={setMondayData}
        mondayPrefs={mondayPrefs}
        setMondayPrefs={setMondayPrefsWrapped}
        githubConnected={githubConnected}
        setGithubConnected={setGithubWrapped}
        githubPrefs={githubPrefs}
        setGithubPrefs={setGithubPrefsWrapped}
        teamMembers={teamMembers}
        canManageWorkspace={canManageWorkspace}
        setSlackPrefs={setSlackPrefsWrapped}
        pullSlack={pullSlack}
        teamsConnected={teamsConnected}
        setTeamsConnected={setTeamsConnectedWrapped}
        teamsPrefs={teamsPrefs}
        setTeamsPrefs={setTeamsPrefsWrapped}
        pullTeams={pullTeams}
        jiraPrefs={jiraPrefs}
        apiBase={apiBase}
      />
      </fieldset>
      {/* Team directory */}
      <div id="cr-team-directory" />
      <TeamDirectorySettings
        teamMembers={teamMembers}
        setTeamMembers={setTeamMembersWrapped}
        apiBase={apiBase}
        currentUserEmail={currentUserEmail}
        workspaceOwnerEmail={workspaceOwnerEmail}
        canManageWorkspace={canManageWorkspace}
        canEditDirectory={canEditDirectory}
        canInviteTeammates={canInviteTeammates}
        canEditOwnConnections={!!workspacePermissions?.canEditOwnConnections}
        // The access map refused the last mirror (this page's save or the
        // bootstrap mirror in App.js), or the document save itself refused an
        // address the directory added. Held in App so it survives navigation.
        directorySyncError={directorySyncError}
        onDismissDirectorySyncError={() => reportDirectorySync(null)}
        // "Try again" on an access-map refusal: re-sends the mirror of the
        // directory as it stands (the explicit clear when it no longer has
        // anybody in it but the access map still might).
        onRetryDirectorySync={
          canEditDirectory ? () => mirrorDirectory(teamMembers) : null
        }
        // "Remove their access" for addresses the access map still holds but
        // the directory does not. Owner only, and only ever on that click: an
        // ordinary sync of the current directory (whose replace semantics drop
        // every unlisted row), or the explicit clear when nobody is left.
        onRemoveStrandedAccess={
          canEditDirectory
            ? () => mirrorDirectory(teamMembers, { explicitClear: true })
            : null
        }
        directoryMirrorVersion={directoryMirrorVersion}
        directorySavePending={saveStatus === "saving"}
      />
      <fieldset
        disabled={!canManageWorkspace}
        style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
      >
      {/* Project Scope */}
      <div className="panelSub">
        <h3 className="crTitle"><CrIcon name="scope" size={18} />Project scope</h3>
        <p className="muted" style={{ marginTop: 4 }}>
          Describe the in/out-of-scope work, priorities, guardrails, and key
          stakeholders. BossDrop will inject this into every automation AI
          decision.
        </p>
        <textarea
          className="inputFancy"
          rows={8}
          placeholder="e.g., Only focus on Checkout v2 migration; exclude marketing experiments; design approvals must include Dana; SLO for incidents P1 ≤ 4h..."
          value={projectScope || ""}
          onChange={(e) =>
            setProjectScopeWrapped(e.target.value.slice(0, 25000))
          }
          style={{ width: "100%", resize: "vertical" }}
        />
        <div className="muted small" style={{ marginTop: 6 }}>
          {`${(projectScope || "").length}/25000 characters`}
        </div>
        <div className="crCard--nested crDropzone" style={{ marginTop: 16 }}>
          <input
            ref={contextFileInputRef}
            type="file"
            multiple
            onChange={(e) => handleContextFilesSelected(e.target.files)}
            style={{ display: "none" }}
          />
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>AI context files</div>
              <div className="muted small" style={{ marginTop: 4, maxWidth: 760 }}>
                Upload specs, PDFs, spreadsheets, screenshots, notes, or other
                source material. BossDrop will condense them and feed that
                context into its AI decisions.
              </div>
            </div>
            <button
              type="button"
              onClick={handleOpenContextFilePicker}
              disabled={!canManageWorkspace || contextFilesBusy}
              className="btn primary"
            >
              {contextFilesBusy ? "Processing..." : "Upload files"}
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 10 }}>
            {`${contextFilesSafe.length}/${MAX_AI_CONTEXT_FILES} files added`}
          </div>
          {contextFilesStatus ? (
            <div className="muted small" style={{ marginTop: 8 }}>
              {contextFilesStatus}
            </div>
          ) : null}
          {contextFilesError ? (
            <div
              className="small"
              style={{
                marginTop: 8,
                // Token, not a hardcoded dark-theme red: #ff8f8f sits at about
                // 2:1 on the light theme's white card.
                color: "var(--state-bad)",
                fontWeight: 600,
              }}
            >
              {contextFilesError}
            </div>
          ) : null}
          {contextFilesSafe.length ? (
            <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
              {contextFilesSafe.map((file) => (
                <div key={file.id} className="crTile">
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                        {file.name}
                      </div>
                      <div className="muted small" style={{ marginTop: 4 }}>
                        {[file.kind, formatFileBytes(file.sizeBytes), file.sourceRef]
                          .filter(Boolean)
                          .join(" | ")}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveContextFile(file.id)}
                      className="btn btnDangerGhost"
                    >
                      Remove
                    </button>
                  </div>
                  {file.summary ? (
                    <div style={{ marginTop: 10, lineHeight: 1.45 }}>{file.summary}</div>
                  ) : null}
                  {file.stakeholders?.length || file.deadlines?.length ? (
                    <div className="muted small" style={{ marginTop: 8 }}>
                      {file.stakeholders?.length
                        ? `Stakeholders: ${file.stakeholders.slice(0, 4).join(", ")}`
                        : ""}
                      {file.stakeholders?.length && file.deadlines?.length ? " | " : ""}
                      {file.deadlines?.length
                        ? `Deadlines: ${file.deadlines.slice(0, 3).join(", ")}`
                        : ""}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="muted small" style={{ marginTop: 12 }}>
              No extra files yet.
            </div>
          )}
        </div>
      </div>

      <div className="panelSub">
        {/* A top-level card now, so it heads with .crTitle like Fine tuning
            and Connectors rather than .crCardHead__title. These hours are not
            an automation: status pings, the polling gate, the decision
            brain's prompt and the inbox's daily-cap reset all read them, so
            filing them under any one of those hid them from the others.
            Meetings book inside them too (one setting, by the founder's
            call); running around the clock is the separate polling switch. */}
        <h3 className="crTitle">
          <CrIcon name="schedule" size={18} />
          Business hours
        </h3>
        <div style={{ maxWidth: "88ch" }}>
          <div className="muted small crCardHead__sub">
            Your team's working hours. BossDrop books meetings and sends
            status pings inside them. A time someone asks for is still booked
            as asked. Ask BossDrop for something in Slack or Teams outside
            them and it checks with you first. When{" "}
            <em>Only poll during business hours</em> (Fine tuning, Polling
            rate) is on, BossDrop also only runs inside them; leave it off to
            keep BossDrop checking around the clock.
            </div>
            {bhRoundTheClock ? (
              <div className="muted small" style={{ marginTop: 6, color: "var(--state-warn, #b45309)" }}>
                These hours cover nights or weekends, so BossDrop can book
                meetings then. Set your real working hours to keep meetings in
                the working day.
              </div>
            ) : null}
            {pollGateActive ? (
              <>
                <div className="muted small" style={{ marginTop: 8 }}>
                  <span className="chip ok">Polling follows these hours</span>{" "}
                  {ticksEffective.toLocaleString()} ticks a week instead of{" "}
                  {ticksAlways.toLocaleString()}. Email, chat commands and
                  approvals still run at any hour.
                </div>
                {extraPollZones.length ? (
                  <div className="muted small" style={{ marginTop: 8 }}>
                    Also awake {pollWindow.start}–{pollWindow.end} in{" "}
                    {extraPollZones.join(", ")}, so teammates there still get
                    their status ping on time.
                  </div>
                ) : null}
                {pollCoverage.disjointZones.length ? (
                  <div className="muted small" style={{ marginTop: 8 }}>
                    <span className="chip warn">
                      Working days do not overlap
                    </span>{" "}
                    {pollCoverage.disjointZones.join(", ")}{" "}
                    {pollCoverage.disjointZones.length === 1 ? "is" : "are"}{" "}
                    far enough from {pollWindow.tz} that these hours never
                    coincide. BossDrop polls for both, so nothing is dropped,
                    but it is awake roughly twice as long as this card's hours
                    imply, and a teammate there is pinged while your own day
                    is closed.
                  </div>
                ) : null}
              </>
            ) : pollBusinessHoursOnly ? (
              <div className="muted small" style={{ marginTop: 8 }}>
                <span className="chip warn">Not in effect</span>{" "}
                Business-hours polling is switched on, but these hours are
                incomplete, so BossDrop is still polling around the clock
                rather than stopping on its own. The values below are
                suggestions BossDrop has not saved: set the timezone, start,
                end and at least one day to store them.
              </div>
            ) : (
              <div className="muted small" style={{ marginTop: 8 }}>
                These hours do not currently limit when BossDrop runs. Turn on{" "}
                <em>Only poll during business hours</em> under Fine tuning →
                Polling rate to cut AI cost and after-hours noise.
              </div>
            )}
        </div>

        <div className="crFieldRow" style={{ marginTop: 12 }}>
          {/* Timezone */}
          <label className="crField">
            <span className="crFieldLabel">
              Timezone
            </span>
            <FancyTZSelect
              value={bh.tz}
              onChange={(v) =>
                setSlackPrefsWrapped({
                  ...slackPrefs,
                  businessHours: { ...bh, tz: v },
                })
              }
              options={tzOptions}
              placeholder="Timezone"
            />
          </label>

          {/* Start time */}
          <label className="crField">
            <span className="crFieldLabel">
              Start
            </span>
            <FancyMiniSelect
              value={bh.start}
              onChange={(v) => {
                // ensure end is after start by at least 30 min
                const startM = toMin(v);
                const endM = toMin(bh.end);
                let nextEnd = bh.end;
                if (endM <= startM + 30) {
                  const i = Math.max(TIMES.indexOf(v) + 2, 0); // +30min = +2 steps of 15m
                  nextEnd = TIMES[Math.min(i, TIMES.length - 1)];
                }
                setSlackPrefsWrapped({
                  ...slackPrefs,
                  businessHours: { ...bh, start: v, end: nextEnd },
                });
              }}
              options={TIMES.map((t) => ({ value: t, label: t }))}
              width={"100%"}
            />
          </label>

          {/* End time */}
          <label className="crField">
            <span className="crFieldLabel">
              End
            </span>
            <FancyMiniSelect
              value={bh.end}
              onChange={(v) => {
                // prevent end <= start
                if (toMin(v) <= toMin(bh.start)) {
                  const i = Math.min(
                    TIMES.indexOf(bh.start) + 2,
                    TIMES.length - 1,
                  );
                  v = TIMES[i];
                }
                setSlackPrefsWrapped({
                  ...slackPrefs,
                  businessHours: { ...bh, end: v },
                });
              }}
              options={TIMES.map((t) => ({ value: t, label: t }))}
              width={"100%"}
            />
          </label>

          {/* Days */}
          <label className="crField">
            <span className="crFieldLabel">
              Days
            </span>
            <DayPills
              value={bh.days}
              onChange={(days) =>
                setSlackPrefsWrapped({
                  ...slackPrefs,
                  businessHours: {
                    ...bh,
                    days: days.length ? days : BH_DEFAULT_DAYS,
                  },
                })
              }
            />
          </label>
        </div>
      </div>
      {/* Email tracking domains */}
      <div className="panelSub">
        <h3 className="crTitle"><CrIcon name="tracking" size={18} />Email tracking</h3>
        <EmailTrackingMailboxes
          apiBase={apiBase}
          currentUserEmail={currentUserEmail}
          canManageWorkspace={canManageWorkspace}
        />
        <p className="muted" style={{ marginTop: 12 }}>
          <strong>Tracked domains.</strong> Your team&apos;s addresses are
          always included. Add the domains or people you also want threads
          for: <code style={{ fontSize: 12 }}>@domain.com</code> for an entire
          domain, or a full address like{" "}
          <code style={{ fontSize: 12 }}>name@domain.com</code> for one person.
        </p>

        {/* Input row */}
        <div
          style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}
        >
          <input
            className="inputFancy"
            type="text"
            placeholder="@yourcompany.com or name@yourcompany.com"
            value={emailDomainInput}
            onChange={(e) => setEmailDomainInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              const val = emailDomainInput.trim().toLowerCase();
              if (!val.includes("@")) return;
              const current = Array.isArray(emailTrackingDomains)
                ? emailTrackingDomains
                : [];
              if (current.includes(val)) {
                setEmailDomainInput("");
                return;
              }
              setEmailTrackingDomainsWrapped([...current, val]);
              setEmailDomainInput("");
            }}
            disabled={!canManageWorkspace}
            style={{ flex: "1 1 260px", minWidth: 0 }}
          />
          <button
            type="button"
            disabled={
              !canManageWorkspace ||
              !emailDomainInput.trim().includes("@") ||
              (Array.isArray(emailTrackingDomains) &&
                emailTrackingDomains.includes(
                  emailDomainInput.trim().toLowerCase(),
                ))
            }
            onClick={() => {
              const val = emailDomainInput.trim().toLowerCase();
              if (!val.includes("@")) return;
              const current = Array.isArray(emailTrackingDomains)
                ? emailTrackingDomains
                : [];
              if (current.includes(val)) {
                setEmailDomainInput("");
                return;
              }
              setEmailTrackingDomainsWrapped([...current, val]);
              setEmailDomainInput("");
            }}
            className="btn primary"
            style={{ flexShrink: 0 }}
          >
            Add
          </button>
        </div>

        {/* Chips */}
        {Array.isArray(emailTrackingDomains) && emailTrackingDomains.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 12,
            }}
          >
            {emailTrackingDomains.map((entry) => (
              <div key={entry} className="chip crValueChip">
                <span>{entry}</span>
                <button
                  type="button"
                  disabled={!canManageWorkspace}
                  onClick={() =>
                    setEmailTrackingDomainsWrapped(
                      (emailTrackingDomains || []).filter((v) => v !== entry),
                    )
                  }
                  aria-label={`Remove ${entry}`}
                  className="crValueChip__x"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="muted small" style={{ marginTop: 10 }}>
            No domains added yet.
          </div>
        )}

        {/* How long stored email excerpts are kept (lib/emailRetention.js;
            the server deletes each one this many days after storing it). */}
        <div className="crField" style={{ marginTop: 16 }} data-testid="email-retention">
          <span className="crSubTitle">Keep email excerpts for</span>
          <FancySelect
            value={String(normalizeEmailRetentionDays(emailRetentionDays))}
            onChange={(v) => setEmailRetentionDaysWrapped(normalizeEmailRetentionDays(v))}
            options={EMAIL_RETENTION_OPTIONS}
            ariaLabel="Keep email excerpts for"
            disabled={!canManageWorkspace}
            usePortal={true}
          />
          <span className="muted small">
            BossDrop keeps excerpts of matching threads in its memory so it can
            recall earlier context, and deletes each excerpt this long after
            storing it. Facts and summaries it learned stay until an admin
            clears BossDrop&apos;s memory on the Memory page.
            {!canManageWorkspace ? " Only a workspace admin can change this." : ""}
          </span>
        </div>
      </div>
      </fieldset>
      </div>
      <div
        role="tabpanel"
        id="cr-panel-automations"
        aria-labelledby="cr-tab-automations"
        hidden={section !== "automations"}
      >
      <fieldset
        disabled={!canManageWorkspace}
        style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
      >
      {/* Approvals */}
      <div className="panelSub">
        <h3 className="crTitle"><CrIcon name="automation" size={18} />Approvals</h3>{" "}
        <p className="muted" style={{ marginTop: 4 }}>
          Choose whether enabled automations run immediately or wait for human
          approval.
        </p>
        <div className="formRow checks checksPills">
          <label
            className="toolOption"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <input
              type="radio"
              name="automationMode"
              value="auto"
              checked={automationMode === "auto"}
              onChange={() => setAutomationModeWrapped("auto")}
            />
            <span>
              <strong>Automated</strong>
              <div className="muted small">
                Enabled automations can run write actions automatically.
              </div>
            </span>
          </label>
          <label
            className="toolOption"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <input
              type="radio"
              name="automationMode"
              value="manual"
              checked={automationMode === "manual"}
              onChange={() => setAutomationModeWrapped("manual")}
            />
            <span>
              <strong>Manual</strong>
              <div className="muted small">
                Write actions wait for approve or deny in Audit Logs.
              </div>
            </span>
          </label>
        </div>
      </div>
      </fieldset>
      {/* Workflows the customer writes in their own words, built and run by the
          decision layer. Its own routes, because the server stamps when each was
          turned on, so it does not save through the workspace doc. Outside the admin
          fieldset so a member can open one and read it; it holds its own controls to
          admins. */}
      <CustomAutomations
        apiBase={apiBase}
        canManageWorkspace={canManageWorkspace}
        teamMembers={teamMembers}
        onScreen={section === "automations"}
        settingsSaves={settingsSaves}
      />
      {/* Automation Rules (restyled) */}
      {/* Automation Rules (clear + status-aware) */}
      {/* id: the Connectors card links here ("Choose which tool each
          automation uses on its card in Control Room > Automations"). */}
      <div className="panelSub" id="cr-automations">
        <h3 className="crTitle"><CrIcon name="automations" size={18} />Built-in automations</h3>
        <PilotBaselineBanner
          state={pilotData.state}
          link={
            <Link
              to="/kpis#pilot"
              style={{ color: "inherit", fontWeight: 600, textDecoration: "underline" }}
            >
              See the pilot on the KPIs page
            </Link>
          }
        />
        {/*         <div className="infoBanner" style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>How this works</div>
          <div className="muted small">
            1) Connect tools below → 2) Turn <strong>Automation</strong> On → 3)
            Enable the specific automations here.
            <br />
            If Automation is Off, everything is paused (even if toggles are On).
          </div>
        </div> */}
        {(() => {
          const globalAuto = automationModeEffective === "auto";

          // The tool each automation really uses (its own pick when that tool
          // is connected, else the workspace's), so each chip shows that
          // tool's logo. Same rules as the worker; see lib/automationChips.js.
          const chipPrefs = automationToolPrefs || {};
          const chatConnected = {
            teams: teamsToolConnected,
            slack: slackToolConnected,
          };
          const taskReady = {
            planner: plannerConnected,
            asana: asanaToolConnected,
            jira: jiraToolConnected,
            linear: linearToolConnected,
            clickup: clickupToolConnected,
            notion: notionToolConnected,
            monday: mondayToolConnected,
          };

          const rules = [
            {
              key: "autoPingsEnabled",
              purpose: "dailyStatus",
              icon: "pings",
              title: "Daily status pings",
              description:
                "Sends short check-ins for blockers + next update. (Only during business hours.)",
              requiresOk: !!chatBackendEffective,
              chips: dailyPingChips(
                chatToolFor(
                  chipPrefs.dailyStatusChat,
                  chatConnected,
                  chatBackendEffective,
                ),
              ),
            },
            {
              key: "autoMeetingsEnabled",
              purpose: "meetings",
              icon: "meetings",
              title: "Auto-create meetings",
              description:
                "Schedules only when needed and tries to avoid duplicates. Moves, renames or cancels a meeting it booked when someone asks, and adds to its agenda on its own; a cancel it suggests itself always waits for approval.",
              // The calendar chips read the owner's connections (read once,
              // above); the status stays as it was.
              requiresOk: true,
              chips: meetingChips(meetingCalendars, calendarWriteBackend),
            },
            {
              key: "autoTasksEnabled",
              purpose: "tasks",
              icon: "tasks",
              title: "Auto-create & update tasks",
              description:
                "Creates new tasks, updates matching existing tasks, and de-dupes against existing work items.",
              requiresOk: !!taskBackendEffective,
              chips: taskChips(
                taskToolFor(
                  chipPrefs.autoTasksBackend,
                  taskReady,
                  taskBackendEffective,
                ),
              ),
            },
            {
              key: "autoEscalationsEnabled",
              purpose: "escalations",
              icon: "escalations",
              title: "Auto-escalations",
              description:
                "Escalates when a material risk persists. Delivers via chat if possible, email fallback if needed.",
              requiresOk: true,
              chips: escalationChips({
                delivery: chipPrefs.escalationsDelivery,
                chat: chatToolFor(
                  chipPrefs.escalationsChat,
                  chatConnected,
                  chatBackendEffective,
                ),
              }),
            },
          ];

          // Mirrors effectiveReplyBehavior() in server/utils/aiInboxPolicy.js:
          // a reply to a team member who wrote in follows the inbox's own
          // reply mode, in manual mode too (an answer to the person talking
          // to BossDrop needs no approval).
          const inboxReplyBehavior =
            !inbox.enabled || inbox.replyMode === "off"
              ? "off"
              : inbox.replyMode === "approval"
                ? "approval"
                : "auto";

          function computeStatus({ enabled, requiresOk, purpose, replyBehavior }) {
            if (!enabled) return { label: "Off", tone: "muted" };
            if (!requiresOk) return { label: "Needs connector", tone: "bad" };
            if (replyBehavior) {
              // replyMode supersedes executionModes for this card.
              if (replyBehavior === "off") {
                return { label: "Never replies", tone: "warn" };
              }
              if (replyBehavior === "approval") {
                return { label: "Approval required", tone: "warn" };
              }
              return { label: "Enabled", tone: "ok" };
            }
            if (!globalAuto) return { label: "Manual approval", tone: "warn" };
            if (executionModeFor(purpose) === "approval") {
              return { label: "Approval required", tone: "warn" };
            }
            return { label: "Enabled", tone: "ok" };
          }

          const inboxStatus = computeStatus({
            enabled: inbox.enabled,
            requiresOk: true,
            purpose: "emailReplies",
            replyBehavior: inboxReplyBehavior,
          });

          return (
            <>
              <fieldset
                disabled={!canManageWorkspace}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
              <div className="rulesGrid">
              {rules.map((r) => {
                const isInbox = r.key === "__aiInbox";
                const enabled = isInbox
                  ? inbox.enabled
                  : !!(slackPrefs?.[r.key] ?? true);
                const s = computeStatus({
                  enabled,
                  requiresOk: r.requiresOk,
                  purpose: r.purpose,
                  replyBehavior: isInbox ? inboxReplyBehavior : null,
                });

                return (
                  <AutomationRuleCard
                    key={r.key}
                    icon={r.icon}
                    title={r.title}
                    description={r.description}
                    enabled={enabled}
                    onToggle={(checked) =>
                      isInbox
                        ? setInboxKey("enabled", checked)
                        : setSlackPrefsWrapped({
                            ...slackPrefs,
                            [r.key]: checked,
                          })
                    }
                    statusLabel={s.label}
                    statusTone={s.tone}
                    chips={r.chips}
                  >
                    <div className="crFieldRow">
                      {isInbox ? null : renderExecutionModeControl(r.purpose)}
                      {isInbox ? renderAiInboxControls(globalAuto) : null}
                      {(() => {
                      if (isInbox) return null;
                      const prefs = automationToolPrefs || {};
                      const setPref = (k, v) => {
                        const next = { ...prefs };
                        if (!v || v === "auto") delete next[k];
                        else next[k] = v;
                        setAutomationToolPrefsWrapped(next);
                      };

                      // Which card is this? Each card that delivers through
                      // a tool picks that tool itself (ToolChoiceField, rows
                      // from lib/toolsInUse.js).
                      if (r.key === "autoPingsEnabled") {
                        return (
                          <ToolChoiceField
                            id="tool-dailyStatus"
                            label="Chat tool"
                            ariaLabel="Daily status pings chat tool"
                            help="Pings and clarifying questions go here."
                            row={chatToolsRow({
                              connected: chatConnected,
                              own: prefs.dailyStatusChat,
                              workspace: chatWriteBackend,
                            })}
                            onChange={(v) => setPref("dailyStatusChat", v)}
                          />
                        );
                      }

                      if (r.key === "autoMeetingsEnabled") {
                        return (
                          <div
                            style={{
                              display: "grid",
                              gap: 10,
                              alignContent: "start",
                            }}
                          >
                            <ToolChoiceField
                              id="tool-meetingCalendar"
                              label="Meeting calendar"
                              help="Meetings someone asks for are booked from their own calendar when they've connected one. BossDrop's own meetings use this calendar."
                              row={meetingCalendarRow({
                                organizers: meetingOrganizers.organizers,
                                saved: meetingCalendarEmail,
                                status: meetingOrganizers.status,
                              })}
                              onChange={(v) =>
                                setMeetingCalendarEmailWrapped(v || null)
                              }
                            />
                            <ToolChoiceField
                              id="tool-meetings"
                              label="Calendar"
                              ariaLabel="Auto-create meetings calendar"
                              help={
                                meetingPerson
                                  ? `Which of ${meetingWhose} calendars BossDrop books on.`
                                  : "Meetings go on the workspace owner's calendar."
                              }
                              row={calendarToolsRow({
                                calendars: meetingCalendars,
                                saved: calendarWriteBackend,
                                whose: meetingWhose,
                              })}
                              onChange={(v) =>
                                setCalendarWriteBackendWrapped(v || null)
                              }
                            />
                          </div>
                        );
                      }

                      if (r.key === "autoTasksEnabled") {
                        return (
                          <ToolChoiceField
                            id="tool-tasks"
                            label="Task tool (new tasks)"
                            help="Updates stay in the tool the task is already in."
                            row={taskToolsRow({
                              states: taskToolStateList,
                              saved: taskWriteBackend,
                              override: prefs.autoTasksBackend,
                            })}
                            onChange={chooseTaskTool}
                          />
                        );
                      }

                      if (r.key === "autoEscalationsEnabled") {
                        const delivery = prefs.escalationsDelivery || "auto";

                        const deliveryChoices = [
                          {
                            value: "auto",
                            label: "Auto (chat first, email fallback)",
                          },
                          { value: "email", label: "Email" },
                          ...(chatBackendEffective
                            ? [{ value: "chat", label: "Chat only" }]
                            : []),
                        ];

                        return (
                          <div
                            style={{
                              display: "grid",
                              gap: 10,
                              alignContent: "start",
                            }}
                          >
                            <AutomationToolPicker
                              label="Escalation delivery"
                              value={delivery}
                              onChange={(v) =>
                                setPref("escalationsDelivery", v)
                              }
                              options={deliveryChoices}
                              help="Auto = chat first, then email if chat fails. Chat only = never email. Email = never DM."
                            />

                            {delivery !== "email" ? (
                              <ToolChoiceField
                                id="tool-escalations"
                                label="Chat tool"
                                ariaLabel="Auto-escalations chat tool"
                                row={chatToolsRow({
                                  connected: chatConnected,
                                  own: prefs.escalationsChat,
                                  workspace: chatWriteBackend,
                                })}
                                onChange={(v) => setPref("escalationsChat", v)}
                              />
                            ) : null}
                          </div>
                        );
                      }

                      return null;
                    })()}
                    </div>
                  </AutomationRuleCard>
                );
              })}
              </div>
              {/* Exec report: its own switch + schedule */}
              <div className="panelSub" style={{ marginTop: 12 }}>
                {/* Same header shape AND same title treatment as the rule cards
                    above: crCardHead wrapping a __main (title + description) and an
                    __aside for the chip and switch. The cards in the Automations card
                    -- the rule cards, this one and Meeting notes -- all head with
                    .crCardHead__title. crSubTitle is
                    gone: it made this card 12px uppercase muted next to "Daily
                    status pings" in full-contrast 700, two heading treatments inside
                    one card. (Polling rate and Autonomy are NOT siblings of this
                    card; they sit in the separate "Fine tuning" section further down.) */}
                <div className="crCardHead">
                  <div className="crCardHead__main">
                    <div className="ruleTitle crCardHead__title">
                      <CrIcon name="report" />
                      Executive report
                    </div>
                    <div className="muted small crCardHead__sub">
                      Sends the exec report on the schedule below. Saved even if
                      turned off.
                    </div>
                  </div>

                  <div className="crCardHead__aside">
                    <span className={`chip ${execReportStatus.tone}`}>
                      {execReportStatus.label}
                    </span>
                    <input
                      className="switchInput"
                      type="checkbox"
                      checked={autoReportsEnabled}
                      onChange={(e) =>
                        setSlackPrefsWrapped({
                          ...slackPrefs,
                          autoReportsEnabled: e.target.checked,
                        })
                      }
                      aria-label="Toggle executive report automation"
                    />
                  </div>
                </div>
                {(() => {
                  const prefs = automationToolPrefs || {};
                  const setPref = (k, v) => {
                    const next = { ...prefs };
                    if (!v || v === "auto") delete next[k];
                    else next[k] = v;
                    setAutomationToolPrefsWrapped(next);
                  };

                  const deliveryChoices = [
                    // Matches the worker: auto always emails, and also DMs the
                    // report when a chat tool and an exec recipient exist.
                    { value: "auto", label: "Auto (email, plus a chat DM)" },
                    { value: "email", label: "Email" },
                    ...(chatBackendEffective
                      ? [{ value: "chat", label: "Chat DM" }]
                      : []),
                  ];

                  // The report's own chat tool (weeklyReportChat), shown
                  // whenever its delivery can include a chat DM.
                  const chatField = (
                    <ToolChoiceField
                      id="tool-weeklyReport"
                      label="Chat tool"
                      ariaLabel="Executive report chat tool"
                      row={chatToolsRow({
                        connected: {
                          teams: teamsToolConnected,
                          slack: slackToolConnected,
                        },
                        own: prefs.weeklyReportChat,
                        workspace: chatWriteBackend,
                      })}
                      onChange={(v) => setPref("weeklyReportChat", v)}
                    />
                  );

                  if (!chatBackendEffective) {
                    // No chat tool, so the report is email only; the field
                    // says what to connect.
                    return (
                      <div className="crFieldRow" style={{ marginTop: 12 }}>
                        {renderExecutionModeControl("weeklyReport")}
                        {chatField}
                      </div>
                    );
                  }

                  const delivery = prefs.weeklyReportDelivery || "auto";
                  return (
                    <div className="crFieldRow" style={{ marginTop: 12 }}>
                      {renderExecutionModeControl("weeklyReport")}
                      <AutomationToolPicker
                        label="Exec report delivery"
                        value={delivery}
                        onChange={(v) => setPref("weeklyReportDelivery", v)}
                        options={deliveryChoices}
                        help="Auto sends the email and also a chat DM. Chat DM sends the DM and emails only if the DM fails. Email never DMs."
                      />

                      {delivery !== "email" ? chatField : null}
                    </div>
                  );
                })()}
                {!autoReportsEnabled && (
                  <div className="muted small" style={{ marginTop: 8 }}>
                    Exec report is <strong>OFF</strong> — schedule is saved but
                    nothing will be sent.
                  </div>
                )}

                <div
                  style={{
                    marginTop: 10,
                    opacity: autoReportsEnabled ? 1 : 0.55,
                    pointerEvents: autoReportsEnabled ? "auto" : "none",
                  }}
                >
                  {/* your existing schedule controls UI */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      {/* Where the report actually goes. The DM goes only to
                          the exec recipient, so without one it is email. */}
                      <AutomationChips
                        chips={[
                          `Schedule: ${reportPreview}`,
                          ...reportDeliveryChips({
                            delivery: automationToolPrefs?.weeklyReportDelivery,
                            chat: chatToolFor(
                              automationToolPrefs?.weeklyReportChat,
                              {
                                teams: teamsToolConnected,
                                slack: slackToolConnected,
                              },
                              chatBackendEffective,
                            ),
                            dmRecipient: !!(
                              Array.isArray(teamMembers) ? teamMembers : []
                            ).find((m) => m?.execRecipient)?.email,
                          }),
                        ]}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <FancyMiniSelect
                        value={reportFreq}
                        onChange={(v) => {
                          setReportFreq(v);
                          const spec = buildReportSchedule({
                            freq: v,
                            day,
                            time,
                            tz,
                          });
                          setSlackPrefsWrapped({ ...slackPrefs, reportCron: spec });
                        }}
                        options={[
                          { value: "daily", label: "Daily" },
                          { value: "three_day", label: "3-day (Mon/Wed/Fri)" },
                          { value: "weekly", label: "Weekly" },
                        ]}
                        width={190}
                      />

                      {reportFreq === "weekly" && (
                        <FancyMiniSelect
                          value={day}
                          onChange={(v) => {
                            setDay(v);
                            setSlackPrefsWrapped({
                              ...slackPrefs,
                              reportCron: buildReportSchedule({
                                freq: reportFreq,
                                day: v,
                                time,
                                tz,
                              }),
                            });
                          }}
                          options={DAYS.map(([v, l]) => ({ value: v, label: l }))}
                          width={180}
                        />
                      )}

                      <FancyMiniSelect
                        value={time}
                        onChange={(v) => {
                          setTime(v);
                          setSlackPrefsWrapped({
                            ...slackPrefs,
                            reportCron: buildReportSchedule({
                              freq: reportFreq,
                              day,
                              time: v,
                              tz,
                            }),
                          });
                        }}
                        options={TIMES.map((t) => ({ value: t, label: t }))}
                        width={140}
                      />

                      <FancyTZSelect
                        width={240}
                        value={tz}
                        onChange={(v) => {
                          setTz(v);
                          setSlackPrefsWrapped({
                            ...slackPrefs,
                            reportCron: buildReportSchedule({
                              freq: reportFreq,
                              day,
                              time,
                              tz: v,
                            }),
                          });
                        }}
                        options={tzOptions}
                        placeholder="Timezone"
                      />
                    </div>
                  </div>
                </div>
              </div>{" "}
              </fieldset>
              {/* Full width, not a grid tile: this card carries four selects
                  and a status block, and as a tile it stretched its four
                  neighbours to its own height. Outside the fieldset above because
                  a member can still refresh Inbox status; its switch and controls
                  are held to admins here instead. */}
              <AutomationRuleCard
                icon="email"
                title="Email assistant"
                description="People email ai@bossdrop.ai and BossDrop answers as your manager. It only answers people it recognises in this workspace."
                enabled={inbox.enabled}
                onToggle={(checked) => setInboxKey("enabled", checked)}
                toggleDisabled={!canManageWorkspace}
                statusLabel={inboxStatus.label}
                statusTone={inboxStatus.tone}
                chips={[
                  inboxAddress,
                  `Checks every ${inboxPollMinutes} min`,
                  inboxRepliesToday === null
                    ? `Limit ${inbox.dailyReplyCap}/day`
                    : `Replies today ${inboxRepliesToday}/${inbox.dailyReplyCap}`,
                ]}
              >
                <fieldset
                  disabled={!canManageWorkspace}
                  style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
                >
                  {renderAiInboxControls(globalAuto)}
                </fieldset>
                {renderInboxStatus()}
              </AutomationRuleCard>
            </>
          );
        })()}
        {/* Meeting notes. OUTSIDE the admin fieldset: a member must still be able
            to allow transcript access for their own calendar. The card disables
            its own admin-only controls. */}
        <MeetingNotetakerCard
          apiBase={apiBase}
          value={meetingNotetaker}
          onChange={setMeetingNotetakerWrapped}
          canManageWorkspace={canManageWorkspace}
          currentUserEmail={currentUserEmail}
          workspaceOwnerEmail={workspaceOwnerEmail}
          teamMembers={teamMembers}
          automationMode={automationModeEffective}
        />
      </div>
      </div>
      <div
        role="tabpanel"
        id="cr-panel-tuning"
        aria-labelledby="cr-tab-tuning"
        hidden={section !== "tuning"}
      >
      <fieldset
        disabled={!canManageWorkspace}
        style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
      >
      <div className="panelSub">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 className="crTitle" style={{ margin: 0 }}><CrIcon name="ai" size={18} />AI model</h3>
            <p className="muted" style={{ margin: "4px 0 0" }}>
              Choose which AI stack BossDrop uses for automations, reports, and
              <code>@bossdrop</code> replies.
            </p>
          </div>
          <span className="chip ok">{`${currentAIProviderMeta.label}: ${activeModelLabel}`}</span>
        </div>

        <div className="rulesGrid" style={{ marginTop: 12 }}>
          {AI_PROVIDER_OPTIONS.map((option) => {
            const active = currentAIProvider === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`crOptionCard crCard--nested ${active ? "is-selected" : ""}`}
                onClick={() =>
                  setBrainPrefsWrapped((prev) => {
                    const base = prev && typeof prev === "object" ? prev : {};
                    if (option.value === currentAIProvider) {
                      return { ...base, aiProvider: option.value };
                    }
                    // Model ids belong to one provider, so switching providers
                    // resets both slots to the new provider's default.
                    return {
                      ...base,
                      aiProvider: option.value,
                      planningModel: "",
                      criticModel: "",
                    };
                  })
                }
                aria-pressed={active}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <AIProviderLogo provider={option.value} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900 }}>{option.label}</div>
                      <div className="muted small">{option.vendor}</div>
                    </div>
                  </div>
                  <span className={`chip ${active ? "ok" : ""}`}>
                    {active
                      ? option.value === "claude"
                        ? "Recommended"
                        : "Selected"
                      : catalogForProvider(option.value)
                        ? modelLabelFor(
                            catalogForProvider(option.value),
                            catalogForProvider(option.value).defaultModel,
                          )
                        : option.model}
                  </span>
                </div>
                <div className="muted small">{option.blurb}</div>
              </button>
            );
          })}
        </div>

        {currentProviderCatalog ? (
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
              marginTop: 14,
            }}
          >
            <div className="crField">
              <span className="crSubTitle">Main model</span>
              <FancySelect
                value={planningModelValue}
                onChange={(value) => setBrainModel("planningModel", value)}
                options={planningModelOptions}
                ariaLabel={`${currentAIProviderMeta.label} main model`}
                disabled={!canManageWorkspace}
                usePortal={true}
              />
              <span className="muted small">
                Plans each run, drafts replies, and writes reports.
              </span>
            </div>
            <div className="crField">
              <span className="crSubTitle">Reviewer model</span>
              <FancySelect
                value={criticModelValue}
                onChange={(value) => setBrainModel("criticModel", value)}
                options={criticModelOptions}
                ariaLabel={`${currentAIProviderMeta.label} reviewer model`}
                disabled={!canManageWorkspace}
                usePortal={true}
              />
              <span className="muted small">
                Double-checks planned actions before BossDrop runs them.
              </span>
            </div>
          </div>
        ) : aiModelCatalogFailed ? (
          <p className="muted small" style={{ margin: "12px 0 0" }}>
            Couldn't load the model list. BossDrop keeps using{" "}
            {currentAIProviderMeta.label}'s default model.
          </p>
        ) : null}
        {currentProviderCatalog && !currentProviderCatalog.configured ? (
          <p className="muted small" style={{ margin: "10px 0 0" }}>
            {currentAIProviderMeta.label} isn't enabled on this BossDrop server
            yet, so runs use the other provider until it is.
          </p>
        ) : null}
      </div>
      {/* Calendar connector status */}
      {/*       <div className="panelSub" style={{ marginTop: 16 }}>
        <h3>Calendar Connector</h3>
        <p className="muted">
          BossDrop asks the backend for Free/Busy. If Google OAuth isn’t set up,
          the server returns mock data so you can still test the UI.
        </p>
        <CalendarStatus />
      </div> */}
        <div className="panelSub">
          <h3 className="crTitle"><CrIcon name="tuning" size={18} />Polling rate</h3>
          <p className="muted" style={{ marginTop: 4 }}>
            How often BossDrop wakes up, reads the workspace and decides what to
            do. Every tick is an AI call, so this is the single biggest lever on
            what BossDrop costs — and on how fast it reacts.
          </p>

          <div style={{ display: "grid", gap: 10, maxWidth: 520 }}>
            <FancyMiniSelect
              value={pollMode === "custom" ? "custom" : String(pollMinutesSafe)}
              onChange={(v) => {
                if (v === "custom") {
                  setPollMode("custom"); // ✅ makes Custom… stick
                  return;
                }
                setPollMode("preset");
                setPollMinutesWrapped(Math.max(1, Number(v) || 5));
              }}
              options={[
                ...POLL_PRESETS.map((m) => ({
                  value: String(m),
                  label: m === 1 ? "Every 1 minute" : `Every ${m} minutes`,
                })),
                { value: "custom", label: "Custom…" },
              ]}
              width={"100%"}
            />

            {pollMode === "custom" && (
              <label className="crField">
                <span className="crFieldLabel">
                  Custom minutes
                </span>
                <input
                  type="number"
                  className="inputFancy"
                  min={1}
                  step={1}
                  value={pollMinutesSafe}
                  onChange={(e) =>
                    setPollMinutesWrapped(
                      Math.max(1, Math.floor(Number(e.target.value) || 1)),
                    )
                  }
                />
                {pollMinutesSafe >= 60 ? (
                  <span className="muted small">
                    Every {formatPollInterval(pollMinutesSafe)}
                  </span>
                ) : null}
              </label>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              marginTop: 14,
              maxWidth: 520,
            }}
          >
            <div className="switchBody" style={{ minWidth: 0 }}>
              <div className="switchLabel">Only poll during business hours</div>
              <div className="muted small">
                Scheduled ticks run only inside the business hours set under Context.
                Email, chat commands, approvals and the exec report still run at
                any hour.
              </div>
            </div>
            <input
              className="switchInput"
              type="checkbox"
              checked={pollBusinessHoursOnly}
              disabled={!canManageWorkspace}
              onChange={(e) =>
                // ONLY the flag. Writing `businessHours` here would commit a
                // timezone nobody picked (the admin's browser zone) plus
                // 09:00-17:30 MON-FRI, and those same hours decide when
                // teammates get their status pings -- a cost toggle must not
                // quietly decide that (meetings book inside them too).
                // With the hours left alone, an
                // incomplete window shows "Not in effect" below, which is
                // exactly what the worker will do.
                setSlackPrefsWrapped({
                  ...slackPrefs,
                  pollDuringBusinessHoursOnly: e.target.checked,
                })
              }
              aria-label="Toggle business-hours-only polling"
            />
          </div>

          <div className="muted small" style={{ marginTop: 10, maxWidth: 520 }}>
            {pollGateActive ? (
              <>
                Right now: about{" "}
                <strong>{ticksEffective.toLocaleString()}</strong> ticks a week
                — every {formatPollInterval(pollMinutesSafe)},{" "}
                {pollWindowLabel}
                {extraPollZones.length
                  ? ` (plus the same hours in ${extraPollZones.join(", ")} for teammates there)`
                  : ""}
                . That is {ticksSavedPct}% fewer than the{" "}
                {ticksAlways.toLocaleString()} it would run around the clock.
              </>
            ) : (
              <>
                Right now: about <strong>{ticksAlways.toLocaleString()}</strong>{" "}
                ticks a week — every {formatPollInterval(pollMinutesSafe)},
                around the clock.
              </>
            )}
          </div>

          {pollBusinessHoursOnly && !bhUsableForPolling && (
            <div className="muted small" style={{ marginTop: 8, maxWidth: 520 }}>
              <span className="chip warn">Not in effect</span> Your business
              hours are incomplete, so BossDrop is still polling around the
              clock rather than stopping on its own. Set a timezone, a start and
              end time, and at least one day under{" "}
              <strong>Business hours</strong> in Context to make this take effect.
              This switch deliberately does not pick those for you, because the
              same hours also decide when BossDrop checks in with your team.
            </div>
          )}

          {/* Not gated on the toggle: a workspace polling every minute around
              the clock is the most expensive setup there is, and it must not
              have to switch the cost lever ON before the cost warning appears. */}
          {ticksEffective > baselineTicksPerWeek && (
            <div className="muted small" style={{ marginTop: 8, maxWidth: 520 }}>
              {pollGateActive ? (
                <>
                  <span className="chip warn">Still expensive</span> Even
                  limited to business hours, every{" "}
                  {formatPollInterval(pollMinutesSafe)} is about{" "}
                  {ticksEffective.toLocaleString()} ticks a week — more than the{" "}
                  {baselineTicksPerWeek.toLocaleString()} a 5-minute cadence
                  runs around the clock. Business hours alone will not make this
                  cheap; 5 minutes or longer will.
                </>
              ) : (
                <>
                  <span className="chip warn">Expensive</span> Every{" "}
                  {formatPollInterval(pollMinutesSafe)} around the clock is
                  about {ticksEffective.toLocaleString()} ticks a week — more
                  than the {baselineTicksPerWeek.toLocaleString()} a 5-minute
                  cadence runs. Every tick is an AI call; a longer interval, or
                  business-hours-only polling, cuts that directly.
                </>
              )}
            </div>
          )}
        </div>
        <div className="panelSub">
          <h3 className="crTitle"><CrIcon name="shield" size={18} />Autonomy</h3>
          <p className="muted" style={{ marginTop: 4 }}>
            How much BossDrop is allowed to do on its own each time it checks in.
            This controls <strong>how sure</strong> it must be before acting
            without you, <strong>how much</strong> it may do in one pass, and{" "}
            <strong>how soon</strong> it may repeat itself.
          </p>
          <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>
            This is not how often it runs — that is the{" "}
            <strong>polling rate</strong> above.
          </p>
          <div style={{ maxWidth: 520, marginTop: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
                fontSize: 12,
                color: "var(--muted)",
              }}
            >
              <span>Ask first (0)</span>
              <span style={{ fontWeight: 700, color: "var(--text)" }}>
                {aggressionLevel} · {autonomyPreview.label}
              </span>
              <span>Act freely (100)</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={aggressionLevel}
              disabled={!canManageWorkspace}
              onChange={(e) => setAggressionLevel(e.target.value)}
              style={{ width: "100%", accentColor: "var(--accent, #b7a4e6)" }}
              aria-label="Autonomy level"
            />
            {/* Show what the dial actually resolves to, so it is inspectable
                rather than a vibe. These mirror computeAggressionBrainPrefs. */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 10,
                marginTop: 12,
              }}
            >
              {[
                {
                  k: "Acts without asking above",
                  v: `${Math.round(autonomyPreview.autoExecuteConfidence * 100)}% confidence`,
                },
                {
                  k: "Max actions per check-in",
                  v: `${autonomyPreview.maxActionsPerTick}`,
                },
                {
                  k: "Won't repeat itself for",
                  v: autonomyPreview.repeatWindow,
                },
              ].map((item) => (
                <div key={item.k} className="crTile">
                  <div className="crStat__k">{item.k}</div>
                  <div className="crStat__v">{item.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="panelSub">
          <h3 className="crTitle"><CrIcon name="decision" size={18} />Decision confidence</h3>
          <p className="muted" style={{ marginTop: 4 }}>
            Set confidence thresholds (0-100) used by automation decisions.
            Advanced overrides — these take precedence over the Autonomy dial
            for individual thresholds.
          </p>
          <div className="midiKnobGrid">
            {confidenceControls.map((c) => {
              const pct = getBrainConfidencePct(c.key);
              return (
                <MidiKnob
                  key={c.key}
                  label={c.label}
                  value={pct}
                  onChange={(nextPct) => setBrainConfidencePct(c.key, nextPct)}
                  help={c.help}
                  disabled={!canManageWorkspace}
                />
              );
            })}
          </div>
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn"
              onClick={resetBrainConfidenceDefaults}
            >
              Reset confidence defaults
            </button>
          </div>
        </div>
      {/* Risk Rules */}
      {/*       <div className="panelSub" style={{ marginTop: 16 }}>
        <h3>Risk Rules</h3>
        <div className="formRow">
          <label>
            Overdue ≥ days
            <input
              type="number"
              className="inputFancy"
              min={1}
              value={overdueDaysThreshold}
              onChange={(e) => setOverdueWrapped(Number(e.target.value) || 1)}
            />
          </label>

          <label>
            Risk amount ≥ $
            <div className="inputGroup">
              <span className="inputIcon" aria-hidden>
                $
              </span>
              <input
                type="number"
                className="inputFancy"
                min={0}
                step={1000}
                value={riskAmountThreshold}
                onChange={(e) => setRiskWrapped(Number(e.target.value) || 0)}
              />
            </div>
          </label>
        </div>
      </div> */}
      </fieldset>
      </div>
    </section>
  );
}

function CalendarStatus() {
  const [s, setS] = React.useState(null);
  React.useEffect(() => {
    fetch("https://api.bossdrop.ai/api/calendar/status", {
      cache: "no-store",
      credentials: "include",
    })
      .then((r) => r.json())
      .then(setS)
      .catch(() => setS(null));
  }, []);
  if (!s) return <p className="muted">Checking…</p>;
  return (
    <div className="muted">
      <div>
        <strong>Calendar API</strong>:{" "}
        {s.connected ? "Connected" : "Not connected (mock)"}
      </div>
      <div style={{ marginTop: 6 }}>
        <div>Google: {s.googleConnected ? "ready" : "not ready"}</div>
        <div>Microsoft: {s.microsoftConnected ? "ready" : "not ready"}</div>
      </div>
      <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
        Per-user status is shown on each teammate row via the Connect badges.
      </div>
    </div>
  );
}
